// TasksView.tsx - Updated with concise detail view style
import React, { useState, useCallback, useEffect } from 'react';
import '../EnterpriseTable.css';
import './TasksView.css';
import { ApproverSelectionPopup } from '../workFlow/ApproverSelectionPopup';
import { ReviewerSelectionPopup } from '../workFlow/ReviewerSelectionPopup';
import { WorkflowGraphPopup } from './WorkflowGraphPopup';
import { ParentRequestDetailPopup } from '../workFlow/ParentRequestDetailPopup';
import { PersonnelViewWrapper } from '../docAI/PersonnelView';
import { SuccessMessageBanner } from '../companyManagement/SuccessMessageBanner';
import { ErrorMessageBanner } from '../companyManagement/ErrorMessageBanner';
// Import types
import {
    AuditLogEntry,
    Task,
    GroupedTask,
    DetailedGatePassRecord,
    AdditionalApproverSelection,
    AdditionalReviewerSelection,
    TasksViewProps,
    Region
} from './TaskTypes';

// Import utilities
import {
    hasSelectedReviewers,
    getSelectedReviewers,
    getTaskTypeLabel,
    getPriorityLabel,
    getPriorityColor,
    formatRelativeTime,
    formatDateOnly,
   // isTaskOverdue,
    getWorkflowStateDisplay,
    getStageInfo,
    getActionLabel,
   // getActionIcon,
   // getActionColor,
    formatPerformerInfo,
    getRequestTypeLabel,
    getPassTypeLabel,
    getPassTypeColor,
    getRequestTypeColor
} from './TaskUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

export const TasksView: React.FC<TasksViewProps> = ({ 
    getUserInfo,
    getAccessToken, 
    onStatusUpdate 
}) => {
    // View mode state - 'list' shows all tasks, 'detail' shows single task
    const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
    const [selectedTaskForDetail, setSelectedTaskForDetail] = useState<GroupedTask | null>(null);
    
    // Basic state
    const [tasks, setTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedTaskType] = useState<string>('');
    const [processingTaskId, setProcessingTaskId] = useState<number | null>(null);
    const [, setDebugInfo] = useState<any>(null);
    
    // Details state
    const [detailedData, setDetailedData] = useState<{ [key: number]: DetailedGatePassRecord }>({});
    const [loadingDetails, setLoadingDetails] = useState<{ [key: number]: boolean }>({});
    const [detailErrors, setDetailErrors] = useState<{ [key: number]: string }>({});
    
    // Region state management
    const [regions, setRegions] = useState<Region[]>([]);

    // Parent request popup state
    const [showParentPopup, setShowParentPopup] = useState(false);
    const [parentPopupRequestId, setParentPopupRequestId] = useState<number | null>(null);
    
    // Audit log state
    const [auditLogs, setAuditLogs] = useState<{ [key: number]: AuditLogEntry[] }>({});
    const [, setLoadingAuditLogs] = useState<{ [key: number]: boolean }>({});
    const [, setAuditLogErrors] = useState<{ [key: number]: string }>({});
    
    // Comments for approval/rejection
    const [taskComments, setTaskComments] = useState<{ [key: number]: string }>({});
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [showCommentsForTask, setShowCommentsForTask] = useState<number | null>(null);

    // Exclusion state
    const [excludedPersonnel, setExcludedPersonnel] = useState<{ [taskId: number]: number[] }>({});
    const [excludedGates, setExcludedGates] = useState<{ [taskId: number]: number[] }>({});
    const [showGateRemovalMode, setShowGateRemovalMode] = useState<{ [taskId: number]: boolean }>({});
    const [showPersonnelRemovalMode, setShowPersonnelRemovalMode] = useState<{ [taskId: number]: boolean }>({});
    // Gates that Gate Approver wants to reverse (re-include after previous exclusion)
    const [reincludedGates, setReincludedGates] = useState<{ [taskId: number]: number[] }>({});
   
    // Additional approver/reviewer selection state
    const [showAdditionalApproverSelection, setShowAdditionalApproverSelection] = useState<number | null>(null);
    const [showAdditionalReviewerSelection, setShowAdditionalReviewerSelection] = useState<number | null>(null);
    const [additionalApproverSelection, setAdditionalApproverSelection] = useState<{ [taskId: number]: AdditionalApproverSelection }>({});
    const [additionalReviewerSelection, setAdditionalReviewerSelection] = useState<{ [taskId: number]: AdditionalReviewerSelection }>({});

    // NEW: Region-specific authorization cache
    // Keyed by gatePassId — stores the result of checking if the company is authorized in the request's region
    const [regionAuthStatus, setRegionAuthStatus] = useState<{ [gatePassId: number]: { loading: boolean; isAuthorized: boolean } }>({});

    // Simplified filtering and sorting
    const [sortBy, setSortBy] = useState<'startDate' | 'dueDate' | 'priority' | 'company' | 'taskType'>('startDate');
    const [filterBy, setFilterBy] = useState<string>('all');
    const [showWorkflowGraph, setShowWorkflowGraph] = useState(false);

    // Personnel detail modal state
    const [viewingPersonnel, setViewingPersonnel] = useState<any>(null);
    const [isLoadingPersonnelDetail, setIsLoadingPersonnelDetail] = useState(false);

    // Help guide visibility state for detail view
    const [helpGuideVisible, setHelpGuideVisible] = useState(true);

    // Badge view state for Security Approval stage
    const [showBadgeView, setShowBadgeView] = useState(false);
    const [badgePhotoUrls, setBadgePhotoUrls] = useState<{ [personnelId: number]: string | null }>({});
    const [loadingBadges, setLoadingBadges] = useState(false);

    // Success/Error banner state
    const [successBanner, setSuccessBanner] = useState({ isVisible: false, message: '' });
    const [errorBanner, setErrorBanner] = useState({ isVisible: false, message: '' });

    // Internal status update handler — shows banners instead of parent callback
    const handleInternalStatusUpdate = useCallback((status: { type: 'success' | 'error' | null; message: string }) => {
        if (status.type === 'success') {
            setSuccessBanner({ isVisible: true, message: status.message });
        } else if (status.type === 'error') {
            setErrorBanner({ isVisible: true, message: status.message });
        } else {
            onStatusUpdate(status);
        }
    }, [onStatusUpdate]);

    const closeSuccessBanner = useCallback(() => {
        setSuccessBanner({ isVisible: false, message: '' });
    }, []);

    const closeErrorBanner = useCallback(() => {
        setErrorBanner({ isVisible: false, message: '' });
    }, []);

    // Helper functions for concise view
    // Helper function to get region display name safely
// Helper function to get region display name safely
const getRegionDisplay = (details: DetailedGatePassRecord | null): string => {
    if (!details) return '—';

    // 1. Check for the Resolved Name from Backend (Best Case)
    if (details.regionName && details.regionName.trim() !== '') {
        return details.regionName;
    }

    // 2. Check for Legacy String (Backward Compatibility)
    if (details.region && typeof details.region === 'string' && isNaN(Number(details.region))) {
        return details.region;
    }

    // 3. Resolve region ID from either regionId or a numeric region field
    const resolvedRegionId = details.regionId || (details.region ? Number(details.region) : null);
    if (resolvedRegionId) {
        if (regions && regions.length > 0) {
            const foundRegion = regions.find(r => r.regionId === resolvedRegionId);
            if (foundRegion) return foundRegion.regionName;
        }
        // 4. Ultimate Fallback: Just show the ID so we know data exists
        return `Region ID: ${resolvedRegionId}`;
    }

    return '—';
};

    const calculateDuration = (startDate: string, endDate: string): string => {
        if (!startDate || !endDate) return '—';
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 for inclusive counting
        return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    };

   

    // Fetch a document SAS download URL from the authenticated endpoint
    const fetchDocumentSasUrl = async (
        personnelId: number,
        fileType: string,
        accessToken: string
    ): Promise<string | undefined> => {
        try {
            const response = await fetch(
                `${API_BASE_URL}/personnel/${personnelId}/file/${fileType}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.downloadUrl) {
                    return data.downloadUrl;
                }
            }
        } catch (err) {
            console.warn(`⚠️ Could not fetch SAS URL for ${fileType}:`, err);
        }
        return undefined;
    };

    // View personnel details in modal
    const handleViewPersonnel = async (personnelId: number) => {
        console.log('🔍 handleViewPersonnel called with personnelId:', personnelId);
        if (!personnelId) {
            console.error('❌ personnelId is falsy:', personnelId);
            return;
        }
        setIsLoadingPersonnelDetail(true);
        try {
            const accessToken = await getAccessToken();
            const url = `${API_BASE_URL}/personnel/${personnelId}`;
            console.log('🔍 Fetching:', url);
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('🔍 Response status:', response.status);

            let personnelData: any;

            if (response.ok) {
                const result = await response.json();
                console.log('✅ Personnel data received:', result);
                personnelData = result.success && result.data ? result.data : result;
            } else {
                const errorText = await response.text();
                console.error('❌ API error:', response.status, errorText);
                try {
                    const errorJson = JSON.parse(errorText);
                    console.error('❌ Error details:', errorJson.details || errorJson.message);
                } catch (e) { /* not JSON */ }
                setIsLoadingPersonnelDetail(false);
                return;
            }

            // Fetch SAS download URLs for documents in parallel
            const [passportSasUrl, visaIdSasUrl] = await Promise.all([
                personnelData.passportDocumentFileName
                    ? fetchDocumentSasUrl(personnelId, 'passport', accessToken)
                    : Promise.resolve(undefined),
                personnelData.visaIdDocumentFileName
                    ? fetchDocumentSasUrl(personnelId, 'visaid', accessToken)
                    : Promise.resolve(undefined)
            ]);

            let legacySasUrl: string | undefined;
            if (!personnelData.passportDocumentFileName && !personnelData.visaIdDocumentFileName && personnelData.documentFileName) {
                legacySasUrl = await fetchDocumentSasUrl(personnelId, 'document', accessToken);
            }

            if (passportSasUrl) personnelData.passportDocumentFileUrl = passportSasUrl;
            if (visaIdSasUrl) personnelData.visaIdDocumentFileUrl = visaIdSasUrl;
            if (legacySasUrl) personnelData.documentFileUrl = legacySasUrl;

            console.log('✅ Setting viewingPersonnel:', personnelData?.fullName || personnelData?.nationalIdNo);
            setViewingPersonnel(personnelData);
        } catch (error) {
            console.error('❌ Error fetching personnel details:', error);
        } finally {
            setIsLoadingPersonnelDetail(false);
        }
    };

    // Load badge photos for all personnel in a request
    const handleShowBadges = async (personnel: any[], gatePassId: number) => {
        setLoadingBadges(true);
        setShowBadgeView(true);
        const photoMap: { [id: number]: string | null } = {};
        try {
            const accessToken = await getAccessToken();
            await Promise.all(
                personnel.filter((p: any) => !p.isExcluded).map(async (person: any) => {
                    try {
                        const url = await fetchDocumentSasUrl(person.personnelId, 'photo', accessToken);
                        photoMap[person.personnelId] = url || null;
                    } catch {
                        photoMap[person.personnelId] = null;
                    }
                })
            );
        } catch (err) {
            console.warn('⚠️ Error loading badge photos:', err);
        }
        setBadgePhotoUrls(photoMap);
        setLoadingBadges(false);
    };

    // Helper: stage-based progress from workflow state (matches backend logic)
    const calculateStageBasedProgress = (currentState: string): number => {
        const stateProgressMap: { [key: string]: number } = {
            'DRAFT':                       0,
            'SUBMITTED':                   10,
            'COMPANY_APPROVER_PENDING':    15,
            'COMPANY_REVIEWER_PENDING':    30,
            'GATE_REVIEWER_PENDING':       40,
            'GATE_APPROVER_PENDING':       55,
            'SECURITY_APPROVER_PENDING':   70,
            'PARTIALLY_APPROVED':          85,
            'FULLY_APPROVED':              100,
            'REJECTED':                    100,
            'RETURNED':                    100,
            'CANCELLED':                   100,
            'EXPIRED':                     100
        };
        return stateProgressMap[currentState] ?? 0;
    };

    // Helper function to render progress bar
    const renderProgressBar = (_workflowProgress: number, currentState: string) => {
        const progress = calculateStageBasedProgress(currentState);
        const isCompleted = currentState === 'FULLY_APPROVED';
        const isRejected = currentState === 'REJECTED';
        
        let progressColor = '#F3694A';
        if (isCompleted) progressColor = '#242424';
        if (isRejected) progressColor = '#242424';

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                    width: '120px',
                    height: '6px',
                    backgroundColor: '#e2e8f0',
                    borderRadius: '3px',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        width: `${progress}%`,
                        height: '100%',
                        backgroundColor: progressColor,
                        borderRadius: '3px',
                        transition: 'width 0.3s ease'
                    }} />
                </div>
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                    {progress}%
                </span>
            </div>
        );
    };

    // Fetch regions for display purposes
    const fetchRegions = useCallback(async () => {
        try {
            const accessToken = await getAccessToken();
            const response = await fetch(`${API_BASE_URL}/regions`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data) {
                    setRegions(result.data);
                }
            }
        } catch (error) {
            console.error('Error fetching regions:', error);
            setRegions([
                { regionId: 1, regionName: 'North America', regionCode: 'NA', isActive: true },
                { regionId: 2, regionName: 'Europe', regionCode: 'EU', isActive: true },
                { regionId: 3, regionName: 'Asia Pacific', regionCode: 'APAC', isActive: true },
                { regionId: 4, regionName: 'Latin America', regionCode: 'LATAM', isActive: true },
                { regionId: 5, regionName: 'Middle East & Africa', regionCode: 'MEA', isActive: true }
            ]);
        }
    }, [getAccessToken]);

    useEffect(() => {
        fetchRegions();
    }, [fetchRegions]);

    // Helper function to get region ID from current task details
    const getCurrentTaskRegionId = (): number | null => {
        if (showAdditionalReviewerSelection && selectedTaskForDetail) {
            const taskDetails = detailedData[selectedTaskForDetail.primaryTask.gatePassId];
            return taskDetails?.regionId || null;
        }
        return null;
    };

    // Load tasks from API
    const loadTasks = useCallback(async () => {
        setLoading(true);
        try {
            const accessToken = await getAccessToken();
            const params = new URLSearchParams();
            if (selectedTaskType) params.append('taskType', selectedTaskType);
            params.append('status', 'PENDING');

            const response = await fetch(`${API_BASE_URL}/my-tasks?${params}`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });

            const data = await response.json();

            if (data.success) {
                setTasks(data.data);
                setDebugInfo(data.userInfo || null);
                
                if (data.data.length === 0) {
                    handleInternalStatusUpdate({ 
                        type: 'success', 
                        message: 'No pending tasks found — your queue is clear.' 
                    });
                }
            } else {
                handleInternalStatusUpdate({ type: 'error', message: data.message || 'Failed to load tasks' });
            }
        } catch (error) {
            console.error('Error loading tasks:', error);
            handleInternalStatusUpdate({ type: 'error', message: 'Error loading tasks' });
        } finally {
            setLoading(false);
        }
    }, [getAccessToken, handleInternalStatusUpdate, selectedTaskType]);

    useEffect(() => {
        loadTasks();
    }, [loadTasks]);

    // Load detailed gate pass information for a task
    const loadTaskDetails = useCallback(async (gatePassId: number, retryCount = 0) => {
        setDetailErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[gatePassId];
            return newErrors;
        });
        
        if (detailedData[gatePassId] && retryCount === 0) {
            return;
        }

        setLoadingDetails(prev => ({ ...prev, [gatePassId]: true }));

        try {
            const accessToken = await getAccessToken();
            const url = `${API_BASE_URL}/gatepass/${gatePassId}`;
            
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 45000);
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const result = await response.json();
                
                if (result.success) {
                    setDetailedData(prev => ({
                        ...prev,
                        [gatePassId]: result.data
                    }));
                } else {
                    const errorMsg = result.message || 'Failed to load gate pass details';
                    setDetailErrors(prev => ({ ...prev, [gatePassId]: errorMsg }));
                }
            } else {
                const errorText = await response.text();
                
                let errorData;
                try {
                    errorData = JSON.parse(errorText);
                } catch (parseError) {
                    errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
                }
                
                if (response.status === 503 && errorData.retryable && retryCount < 3) {
                    setTimeout(() => {
                        loadTaskDetails(gatePassId, retryCount + 1);
                    }, (retryCount + 1) * 2000);
                    return;
                }
                
                let userMessage: string = `Failed to load details (${response.status})`;
                if (response.status === 404) {
                    userMessage = 'Gate pass not found or access denied';
                } else if (response.status === 403) {
                    userMessage = 'Access denied to this gate pass';
                } else if (response.status === 401) {
                    userMessage = 'Authentication failed - please sign in again';
                } else if (response.status === 503) {
                    userMessage = errorData.message || 'Gate pass service temporarily unavailable';
                } else {
                    userMessage = errorData.message || `Failed to load details (${response.status})`;
                }
                
                setDetailErrors(prev => ({ ...prev, [gatePassId]: userMessage }));
            }
        } catch (error) {
            let errorMessage = 'Error loading gate pass details';
            
            if (error instanceof TypeError && error.message.includes('fetch')) {
                errorMessage = 'Network error - unable to connect to server';
                
                if (retryCount < 2) {
                    setTimeout(() => {
                        loadTaskDetails(gatePassId, retryCount + 1);
                    }, (retryCount + 1) * 3000);
                    return;
                }
            } else if (error instanceof Error) {
                errorMessage = error.message;
            }
            
            setDetailErrors(prev => ({ ...prev, [gatePassId]: errorMessage }));
        } finally {
            setLoadingDetails(prev => ({ ...prev, [gatePassId]: false }));
        }
    }, [getAccessToken, detailedData]);

    // =========================================================================
    // NEW: Region-specific authorization check
    // When task details load, check if the company is authorized for the
    // request's region (instead of checking the old company-level flag).
    // =========================================================================
    const checkRegionAuthorization = useCallback(async (gatePassId: number) => {
        const details = detailedData[gatePassId];
        if (!details) return;

        // Already checked or currently loading
        if (regionAuthStatus[gatePassId] && !regionAuthStatus[gatePassId].loading) return;

        // IMPORTANT: We check if the REVIEWER's company (logged-in user) is authorized
        // in the request's region — NOT the submitter's company.
        const userInfo = getUserInfo();
        const companyId = userInfo.companyId;
        const regionId = details.regionId || ((details as any).region ? Number((details as any).region) : null);

        if (!companyId || !regionId) {
            // If companyId or regionId not available, fall back to old company-level flag
            console.warn(`⚠️ Region auth check skipped for gatePass ${gatePassId}: missing companyId (${companyId}) or regionId (${regionId}). Falling back to company-level flag.`);
            setRegionAuthStatus(prev => ({
                ...prev,
                [gatePassId]: {
                    loading: false,
                    isAuthorized: (details as any).isCompanyAuthorized ?? false
                }
            }));
            return;
        }

        // Mark as loading
        setRegionAuthStatus(prev => ({
            ...prev,
            [gatePassId]: { loading: true, isAuthorized: false }
        }));

        try {
            const accessToken = await getAccessToken();
            const response = await fetch(
                `${API_BASE_URL}/companies/${companyId}/authorized/${regionId}`,
                {
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                }
            );

            if (response.ok) {
                const result = await response.json();
                console.log(`🔐 Region auth for gatePass ${gatePassId}: reviewer company ${companyId} (${userInfo.companyName}) in region ${regionId} => ${result.isAuthorized}`);
                setRegionAuthStatus(prev => ({
                    ...prev,
                    [gatePassId]: {
                        loading: false,
                        isAuthorized: !!result.isAuthorized
                    }
                }));
            } else {
                console.error(`❌ Region auth check failed for gatePass ${gatePassId}: HTTP ${response.status}`);
                // On failure, default to NOT authorized (safe — requires reviewer)
                setRegionAuthStatus(prev => ({
                    ...prev,
                    [gatePassId]: { loading: false, isAuthorized: false }
                }));
            }
        } catch (error) {
            console.error(`❌ Region auth check error for gatePass ${gatePassId}:`, error);
            // On error, default to NOT authorized (safe — requires reviewer)
            setRegionAuthStatus(prev => ({
                ...prev,
                [gatePassId]: { loading: false, isAuthorized: false }
            }));
        }
    }, [detailedData, regionAuthStatus, getAccessToken, getUserInfo]);

    // Trigger region auth check when COMPANY_REVIEWER task details finish loading
    useEffect(() => {
        tasks.forEach(task => {
            if (task.taskType === 'COMPANY_REVIEWER' && detailedData[task.gatePassId] && !regionAuthStatus[task.gatePassId]) {
                checkRegionAuthorization(task.gatePassId);
            }
        });
    }, [tasks, detailedData, regionAuthStatus, checkRegionAuthorization]);

    // Load audit log information for a gate pass
    const loadAuditLogs = useCallback(async (gatePassId: number, retryCount = 0) => {
        setAuditLogErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[gatePassId];
            return newErrors;
        });
        
        if (auditLogs[gatePassId] && retryCount === 0) {
            return;
        }

        setLoadingAuditLogs(prev => ({ ...prev, [gatePassId]: true }));

        try {
            const accessToken = await getAccessToken();
            const url = `${API_BASE_URL}/gatepass/${gatePassId}/audit-logs`;
            
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const result = await response.json();
                
                if (result.success) {
                    setAuditLogs(prev => ({
                        ...prev,
                        [gatePassId]: result.data || []
                    }));
                } else {
                    const errorMsg = result.message || 'Failed to load audit logs';
                    setAuditLogErrors(prev => ({ ...prev, [gatePassId]: errorMsg }));
                }
            } else {
                const errorText = await response.text();
                
                let errorData: any;
                try {
                    errorData = JSON.parse(errorText);
                } catch (parseError) {
                    errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
                }
                
                if (response.status === 503 && errorData.retryable && retryCount < 3) {
                    setTimeout(() => {
                        loadAuditLogs(gatePassId, retryCount + 1);
                    }, (retryCount + 1) * 2000);
                    return;
                }
                
                let userMessage: string = `Failed to load audit logs (${response.status})`;
                
                if (response.status === 404) {
                    userMessage = 'Audit logs not available for this gate pass';
                } else if (response.status === 403) {
                    userMessage = 'Access denied to audit logs for this gate pass';
                } else if (response.status === 401) {
                    userMessage = 'Authentication failed - please sign in again';
                } else if (response.status === 503) {
                    userMessage = errorData.message || 'Audit log service temporarily unavailable';
                } else {
                    userMessage = errorData.message || `Failed to load audit logs (${response.status})`;
                }
                
                setAuditLogErrors(prev => ({ 
                    ...prev, 
                    [gatePassId]: userMessage 
                }));
                
                if (response.status === 503) {
                    setAuditLogs(prev => ({
                        ...prev,
                        [gatePassId]: []
                    }));
                }
            }
        } catch (error) {
            let errorMessage: string = 'Error loading audit logs';
            
            if (error instanceof TypeError && error.message.includes('fetch')) {
                errorMessage = 'Network error - unable to connect to server';
                
                if (retryCount < 2) {
                    setTimeout(() => {
                        loadAuditLogs(gatePassId, retryCount + 1);
                    }, (retryCount + 1) * 3000);
                    return;
                }
            } else if (error instanceof Error) {
                errorMessage = error.message;
            }
            
            setAuditLogErrors(prev => ({ ...prev, [gatePassId]: errorMessage }));
            setAuditLogs(prev => ({
                ...prev,
                [gatePassId]: []
            }));
        } finally {
            setLoadingAuditLogs(prev => ({ ...prev, [gatePassId]: false }));
        }
    }, [getAccessToken, auditLogs]);



    // Handle showing grouped task details
    const handleShowGroupedTaskDetail = (grouped: GroupedTask) => {
        const task = grouped.primaryTask;
        // Clear previous detailed data for this task to prevent stale values showing
        setDetailedData(prev => {
            const newData = { ...prev };
            delete newData[task.gatePassId];
            return newData;
        });
        // Clear previous audit logs for fresh data
        setAuditLogs(prev => {
            const newLogs = { ...prev };
            delete newLogs[task.gatePassId];
            return newLogs;
        });
        // Set loading state synchronously BEFORE switching to detail view
        setLoadingDetails(prev => ({ ...prev, [task.gatePassId]: true }));
        setDetailErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[task.gatePassId];
            return newErrors;
        });
        // Clear cached region auth so it re-checks with the logged-in user's company
        setRegionAuthStatus(prev => {
            const newStatus = { ...prev };
            delete newStatus[task.gatePassId];
            return newStatus;
        });
        setSelectedTaskForDetail(grouped);
        setViewMode('detail');
        loadTaskDetails(task.gatePassId, 1);
        loadAuditLogs(task.gatePassId, 1);
    };

    // Handle returning to task list
    const handleBackToList = () => {
        setViewMode('list');
        setSelectedTaskForDetail(null);
        setShowCommentsForTask(null);
        setTaskComments({});
        setShowWorkflowGraph(false);
    };

    // Additional approver/reviewer selection handlers
    const handleAdditionalApproverSelection = (taskId: number, selection: AdditionalApproverSelection) => {
        setAdditionalApproverSelection(prev => ({
            ...prev,
            [taskId]: selection
        }));
        setShowAdditionalApproverSelection(null);
        
        const approverNames = selection.selectedApprovers.map(a => a.userName).join(', ');
        const functionName = selection.function?.functionName || 'Unknown';
        
        handleInternalStatusUpdate({
            type: 'success',
            message: `Additional Approvers Selected: ${functionName} | (${selection.selectedApprovers.length}): ${approverNames}`
        });
    };

    const handleAdditionalReviewerSelection = (taskId: number, selection: AdditionalReviewerSelection) => {
        setAdditionalReviewerSelection(prev => ({
            ...prev,
            [taskId]: selection
        }));
        setShowAdditionalReviewerSelection(null);
        
        if (hasSelectedReviewers(selection)) {
            const reviewers = getSelectedReviewers(selection);
            const reviewerNames = reviewers.map(r => r.userName).join(', ');
            const functionName = selection.function?.functionName || 'Unknown';
            const companyName = selection.company?.companyName || 'Unknown';
            
            handleInternalStatusUpdate({
                type: 'success',
                message: `Additional Reviewers Selected: ${companyName} - ${functionName} | (${reviewers.length}): ${reviewerNames}`
            });
        } else {
            handleInternalStatusUpdate({
                type: 'success',
                message: `No Additional Reviewers Selected - Will proceed to next stage after approval`
            });
        }
    };

    // Exclusion handlers
    const togglePersonnelExclusion = (taskId: number, personnelId: number) => {
        setExcludedPersonnel(prev => {
            const currentExcluded = prev[taskId] || [];
            const isExcluded = currentExcluded.includes(personnelId);
            
            if (isExcluded) {
                return {
                    ...prev,
                    [taskId]: currentExcluded.filter(id => id !== personnelId)
                };
            } else {
                return {
                    ...prev,
                    [taskId]: [...currentExcluded, personnelId]
                };
            }
        });
    };

    const toggleGateExclusion = (taskId: number, gateId: number) => {
        setExcludedGates(prev => {
            const currentExcluded = prev[taskId] || [];
            const isExcluded = currentExcluded.includes(gateId);
            
            if (isExcluded) {
                return {
                    ...prev,
                    [taskId]: currentExcluded.filter(id => id !== gateId)
                };
            } else {
                return {
                    ...prev,
                    [taskId]: [...currentExcluded, gateId]
                };
            }
        });
    };

    const toggleGateRemovalMode = (taskId: number) => {
        setShowGateRemovalMode(prev => ({
            ...prev,
            [taskId]: !prev[taskId]
        }));
    };

    const togglePersonnelRemovalMode = (taskId: number) => {
        setShowPersonnelRemovalMode(prev => ({
            ...prev,
            [taskId]: !prev[taskId]
        }));
    };

    // Toggle re-inclusion of a previously excluded gate (for Gate Approver)
    const toggleGateReinclude = (taskId: number, gateId: number) => {
        setReincludedGates(prev => {
            const current = prev[taskId] || [];
            const isReincluded = current.includes(gateId);
            if (isReincluded) {
                return { ...prev, [taskId]: current.filter(id => id !== gateId) };
            } else {
                return { ...prev, [taskId]: [...current, gateId] };
            }
        });
    };



    // Process a grouped task - sends all taskIds in the group to a batch endpoint
    const processGroupedTask = async (grouped: GroupedTask, decision: 'APPROVED' | 'REJECTED' | 'RETURNED', comments: string) => {
        const primaryTaskId = grouped.primaryTask.taskId;
        setProcessingTaskId(primaryTaskId);
        try {
            const accessToken = await getAccessToken();

            const excludedPersonnelList = excludedPersonnel[primaryTaskId] || [];
            const excludedGatesList = excludedGates[primaryTaskId] || [];
            const reincludedGatesList = reincludedGates[primaryTaskId] || [];

            const requestBody: any = {
                taskIds: grouped.taskIds,
                decision,
                comments
            };

            if (decision === 'RETURNED') {
                requestBody.convertToDraft = true;
            }

            if (excludedPersonnelList.length > 0 || excludedGatesList.length > 0) {
                requestBody.excludedPersonnel = excludedPersonnelList;
                requestBody.excludedGates = excludedGatesList;
            }

            if (reincludedGatesList.length > 0) {
                requestBody.reincludedGates = reincludedGatesList;
            }

            // Additional approvers/reviewers (only for COMPANY_REVIEWER, but included for completeness)
            const task = grouped.primaryTask;
            if (task.taskType === 'COMPANY_REVIEWER' && decision === 'APPROVED') {
                const additionalApprovers = additionalApproverSelection[primaryTaskId];
                const additionalReviewers = additionalReviewerSelection[primaryTaskId];

                if (additionalApprovers?.selectedApprovers?.length > 0) {
                    requestBody.additionalApprovers = {
                        functionId: additionalApprovers.function?.functionId,
                        selectedApprovers: additionalApprovers.selectedApprovers.map(a => ({
                            userId: a.userId,
                            userEmail: a.userEmail,
                            userName: a.userName
                        }))
                    };
                }

                if (additionalReviewers && hasSelectedReviewers(additionalReviewers)) {
                    requestBody.additionalReviewers = {
                        companyId: additionalReviewers.company?.companyId,
                        functionId: additionalReviewers.function?.functionId,
                        selectedReviewers: getSelectedReviewers(additionalReviewers).map(r => ({
                            userId: r.userId,
                            userEmail: r.userEmail,
                            userName: r.userName
                        }))
                    };
                }
            }

            const response = await fetch(`${API_BASE_URL}/tasks/batch-action`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (data.success) {
                if (decision === 'RETURNED' && data.data?.draftId) {
                    handleInternalStatusUpdate({
                        type: 'success',
                        message: `Request returned to submitter and converted to draft #${data.data.draftId} for editing`
                    });
                } else {
                    handleInternalStatusUpdate({ type: 'success', message: data.message });
                }

                // Clear task states for all taskIds in the group
                for (const tid of grouped.taskIds) {
                    setTaskComments(prev => ({ ...prev, [tid]: '' }));
                    setExcludedPersonnel(prev => ({ ...prev, [tid]: [] }));
                    setExcludedGates(prev => ({ ...prev, [tid]: [] }));
                    setReincludedGates(prev => ({ ...prev, [tid]: [] }));
                    setShowGateRemovalMode(prev => ({ ...prev, [tid]: false }));
                    setShowPersonnelRemovalMode(prev => ({ ...prev, [tid]: false }));
                    setAdditionalApproverSelection(prev => ({ ...prev, [tid]: { function: null, selectedApprovers: [] } }));
                    setAdditionalReviewerSelection(prev => ({ ...prev, [tid]: { company: null, function: null, selectedReviewers: [] } }));
                }

                setShowCommentsForTask(null);
                handleBackToList();
                loadTasks();
            } else {
                handleInternalStatusUpdate({ type: 'error', message: data.error || 'Failed to process task' });
            }
        } catch (error) {
            console.error('Error processing grouped task:', error);
            handleInternalStatusUpdate({ type: 'error', message: 'Error processing task' });
        } finally {
            setProcessingTaskId(null);
        }
    };

    // Filter and sort logic
    const filteredTasks = tasks.filter(task => {
        if (filterBy === 'all') return true;
        return task.taskType === filterBy;
    });

    const sortedTasks = [...filteredTasks].sort((a, b) => {
        switch (sortBy) {
            case 'startDate':
                const aStartDate = a.startDate ? new Date(a.startDate).getTime() : Number.MAX_SAFE_INTEGER;
                const bStartDate = b.startDate ? new Date(b.startDate).getTime() : Number.MAX_SAFE_INTEGER;
                return aStartDate - bStartDate;
            case 'dueDate':
                return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
            case 'priority':
                return b.priority - a.priority;
            case 'company':
                return a.companyName.localeCompare(b.companyName);
            case 'taskType':
                return a.taskType.localeCompare(b.taskType);
            default:
                return 0;
        }
    });

    // Group gate-level tasks: consolidate tasks for the same user on the same gatePassId + taskType
    // into a single GroupedTask. Non-gate tasks remain individual.
    const groupTasks = (taskList: Task[]): GroupedTask[] => {
        const gateTaskTypes = ['GATE_APPROVER', 'GATE_REVIEWER'];
        const groups: { [key: string]: Task[] } = {};
        const result: GroupedTask[] = [];

        for (const t of taskList) {
            if (gateTaskTypes.includes(t.taskType)) {
                const key = `${t.gatePassId}_${t.taskType}`;
                if (!groups[key]) groups[key] = [];
                groups[key].push(t);
            } else {
                // Non-gate tasks are always individual
                result.push({
                    primaryTask: t,
                    taskIds: [t.taskId],
                    assignedGateNumbers: t.gateNumber ? [t.gateNumber] : [],
                    gateInfos: t.gateNumber ? [{ taskId: t.taskId, gateNumber: t.gateNumber, gateId: (t as any).gateId }] : [],
                    isGrouped: false
                });
            }
        }

        for (const key of Object.keys(groups)) {
            const groupedTasks = groups[key];
            const primary = groupedTasks[0];
            result.push({
                primaryTask: primary,
                taskIds: groupedTasks.map(t => t.taskId),
                assignedGateNumbers: groupedTasks.map(t => t.gateNumber).filter(Boolean) as string[],
                gateInfos: groupedTasks.map(t => ({ taskId: t.taskId, gateNumber: t.gateNumber || '', gateId: (t as any).gateId })),
                isGrouped: groupedTasks.length > 1
            });
        }

        // Preserve original sort order by using the primary task's position
        const orderMap = new Map(taskList.map((t, i) => [t.taskId, i]));
        result.sort((a, b) => (orderMap.get(a.primaryTask.taskId) ?? 0) - (orderMap.get(b.primaryTask.taskId) ?? 0));

        return result;
    };

    const groupedSortedTasks = groupTasks(sortedTasks);

    // Determine which view to render
    const renderContent = () => {

    // Loading state
    if (loading) {
        return (
            <div style={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '40px',
                textAlign: 'center',
                margin: '20px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
                <div style={{ color: '#64748b', fontSize: '24px', marginBottom: '12px' }}>⏳</div>
                <div style={{ color: '#1e293b', fontSize: '16px', fontWeight: '600', marginBottom: '6px' }}>Loading Tasks</div>
                <div style={{ color: '#64748b', fontSize: '14px' }}>Please wait while we fetch your pending tasks...</div>
            </div>
        );
    }

    // Empty state
    if (tasks.length === 0) {
        return (
            <div style={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '0px',
                padding: '60px 40px',
                textAlign: 'center',
                margin: '20px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
                <div style={{ marginBottom: '20px' }}>
                    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                </div>
                <h3 style={{ color: '#1e293b', marginBottom: '8px', fontSize: '18px', fontWeight: 600 }}>You're All Caught Up</h3>
                <p style={{ color: '#64748b', marginBottom: '24px', fontSize: '14px', maxWidth: '360px', margin: '0 auto 24px' }}>
                    There are no pending tasks assigned to you right now. New tasks will appear here when a gate pass request requires your action.
                </p>
                <button
                    onClick={loadTasks}
                    disabled={loading}
                    style={{
                        padding: '8px 20px',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#F3694A',
                        backgroundColor: '#fef5f3',
                        border: '1px solid #F3694A30',
                        borderRadius: '6px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.6 : 1,
                        transition: 'all 0.15s ease'
                    }}
                >
                    {loading ? 'Checking...' : 'Check Again'}
                </button>
            </div>
        );
    }

    // DETAIL VIEW - Concise style similar to ConciseRequestDetailsView
    if (viewMode === 'detail' && selectedTaskForDetail) {
        const task = selectedTaskForDetail.primaryTask;
        const groupedGateNumbers = selectedTaskForDetail.assignedGateNumbers;
        const details = detailedData[task.gatePassId];
        const isLoadingDetail = loadingDetails[task.gatePassId];
        const detailError = detailErrors[task.gatePassId];
        const logs = auditLogs[task.gatePassId] || [];
        const stageInfo = getStageInfo(task.taskType);
        const canExclude = stageInfo.allowsExclusions;

        // Wizard step mapping
        const workflowSteps = [
            { id: 1, title: 'Company Approvals', desc: 'step 1', types: ['COMPANY_APPROVER', 'COMPANY_REVIEWER'] },
            { id: 2, title: 'Gate Approvals', desc: 'step 2', types: ['GATE_APPROVER', 'GATE_REVIEWER'] },
            { id: 3, title: 'Security Approval', desc: 'step 3', types: ['SECURITY_APPROVER'] },
        ];

        const currentStepId = workflowSteps.find(s => s.types.includes(task.taskType))?.id || 1;

        const getStepState = (stepId: number): 'current' | 'completed' | '' => {
            if (stepId === currentStepId) return 'current';
            if (stepId < currentStepId) return 'completed';
            return '';
        };

        // Help content per task type
        const getTaskHelp = () => {
            switch (task.taskType) {
                case 'COMPANY_APPROVER':
                    return {
                        title: 'Company Approval',
                        description: 'Review the request details and verify they meet the relevant contract requirements. Your approval will advance this request to the next stage in the workflow.',
                        tips: ['Check personnel credentials and gate access needs', 'Verify the scope aligns with the contract', 'Use reject or return for revision if the request cannot be approved', 'Add comments to support the audit trail']
                    };
                case 'COMPANY_REVIEWER': {
                    const crAuthStatus = regionAuthStatus[task.gatePassId];
                    const crIsAuthorized = crAuthStatus ? crAuthStatus.isAuthorized : false;
                    return crIsAuthorized ? {
                        title: 'Company Review (Authorised)',
                        description: 'Your company is authorised in this region. Select an approver from your company to advance the request to Gate Review. You may optionally forward it to another company first.',
                        tips: ['Select an approver from your company (required)', 'Optionally send to another company for additional review', 'You can exclude personnel or gates if needed', 'Reject or return for revision if the request cannot proceed']
                    } : {
                        title: 'Company Review',
                        description: 'Your company is not authorised in this region. You must forward the request to the contracting company for review and also select an approver from your company.',
                        tips: ['Send to another company for review (required)', 'Select an approver from your company (required)', 'You can exclude personnel or gates if needed', 'Reject or return for revision if the request cannot proceed']
                    };
                }
                case 'GATE_APPROVER':
                    return {
                        title: 'Access Area Approval',
                        description: 'This request requires your approval for the access area(s) assigned to you. Review the details and access codes, then endorse or reject.',
                        tips: ['Check the access codes for your assigned gates', 'Endorse to approve the access area request', 'Reject if the request cannot be approved — provide a reason', 'You can also re-include previously excluded gates']
                    };
                case 'GATE_REVIEWER':
                    return {
                        title: 'Gate Review & Endorsement',
                        description: 'Review the access area details for your assigned gates. Your endorsement is required before the request can be forwarded to the approver.',
                        tips: ['Check the access codes assigned to your gates', 'Endorse the request to forward it for approval', 'Reject if the access area request cannot be endorsed — provide a reason', 'You may also exclude specific personnel or gates']
                    };
                case 'SECURITY_APPROVER':
                    return {
                        title: 'Security Approval',
                        description: 'All gate approvals are complete. Provide final security clearance to issue the gate pass.',
                        tips: ['Review consolidated access codes', 'Verify all personnel are cleared', 'Once approved, the gate pass will be issued']
                    };
                default:
                    return {
                        title: 'Task Action',
                        description: 'Review the request details and take the appropriate action.',
                        tips: ['Review all information carefully', 'Add comments for the audit trail']
                    };
            }
        };

        const helpInfo = getTaskHelp();

        return (
            <div style={{ backgroundColor: '#ffffff', minHeight: '100vh', padding: '0px' }}>
                {/* Success/Error Banners */}
                <SuccessMessageBanner
                    message={successBanner.message}
                    isVisible={successBanner.isVisible}
                    onClose={closeSuccessBanner}
                    autoCloseDelay={5000}
                />
                <ErrorMessageBanner
                    message={errorBanner.message}
                    isVisible={errorBanner.isVisible}
                    onClose={closeErrorBanner}
                    autoCloseDelay={7000}
                />

                <div className="task-wizard-layout">

                    {/* ── LEFT: Workflow Steps Sidebar ── */}
                    <div className="task-wizard-sidebar">
                        <div className="task-wizard-steps">
                            {workflowSteps.map((step) => {
                                const state = getStepState(step.id);
                                return (
                                    <div key={step.id} className={`task-step-wrapper ${state}`}>
                                        <div className="task-step-bar" />
                                        <div className="task-step-item">
                                            <span className="task-step-title">{step.title}</span>
                                            <span className="task-step-desc">{step.desc}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* ── CENTER: Task Content ── */}
                    <div className="task-wizard-content">

                        {/* Compact Toolbar */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0 0 12px 0',
                            marginBottom: '16px',
                            borderBottom: '1px solid #e2e8f0'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <button 
                                    onClick={handleBackToList}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '6px 14px',
                                        backgroundColor: 'transparent',
                                        color: '#475569',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        transition: 'all 0.15s ease'
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                >
                                    ← Back
                                </button>
                                <span style={{ color: '#d1d5db', fontSize: '14px' }}>/</span>
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#242424' }}>
                                    Request #{task.gatePassId}
                                </span>
                                <span style={{ color: '#9ca3af', fontSize: '13px' }}>
                                    · {getTaskTypeLabel(task.taskType)}
                                </span>
                                {task.priority >= 2 && (
                                    <span style={{
                                        backgroundColor: getPriorityColor(task.priority) + '15',
                                        color: getPriorityColor(task.priority),
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.03em'
                                    }}>
                                        {getPriorityLabel(task.priority)}
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={() => setShowWorkflowGraph(true)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 14px',
                                    backgroundColor: '#242424',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    transition: 'all 0.15s ease',
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#3a3a3a'; }}
                                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#242424'; }}
                            >
                                🔀 Workflow
                            </button>
                        </div>

                        {/* Page Title */}
                        <h1 style={{
                            fontSize: '24px',
                            fontWeight: 700,
                            color: '#1a1a1a',
                            margin: '0 0 24px 0',
                            padding: 0,
                            lineHeight: 1.3,
                            fontFamily: "'Segoe UI', sans-serif",
                            letterSpacing: '-0.01em',
                        }}>
                            Gate Pass Request Approval
                        </h1>

                {/* Content */}
                {isLoadingDetail ? (
                    <div style={{
                        padding: '40px',
                        textAlign: 'center'
                    }}>
                        <div style={{ color: '#64748b', fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                        <div style={{ color: '#64748b', fontSize: '14px', fontWeight: '500' }}>Loading gate pass details...</div>
                    </div>
                ) : detailError ? (
                    <div style={{ padding: '0px' }}>
                        <div style={{
                            backgroundColor: '#f9f9f9',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '16px',
                            textAlign: 'center'
                        }}>
                            <div style={{ color: '#242424', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                                Failed to Load Details
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '12px' }}>
                                {detailError}
                            </div>
                            <button 
                                onClick={() => loadTaskDetails(task.gatePassId)}
                                style={{
                                    backgroundColor: '#242424',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                ) : details ? (
                    <div style={{
                        padding: '24px 0'
                    }}>

                        {/* Request Info */}
                        <div style={{ marginBottom: '28px' }}>
                            <h3 style={{
                                margin: '0 0 16px 0',
                                fontSize: '15px',
                                fontWeight: '600',
                                color: '#242424'
                            }}>
                                Request Information
                            </h3>
                            
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '14px 32px'
                            }}>
                                <div>
                                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Requester</div>
                                    <div style={{ fontSize: '14px', color: '#242424', fontWeight: '500' }}>{details.name}</div>
                                </div>
                                
                                <div>
                                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Email</div>
                                    <div style={{ fontSize: '14px', color: '#242424', fontWeight: '500' }}>{details.email}</div>
                                </div>
                                
                                <div>
                                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Company</div>
                                    <div style={{ fontSize: '14px', color: '#242424', fontWeight: '500' }}>{details.companyName}</div>
                                </div>
                                
                                <div>
                                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Region</div>
                                    <div style={{ fontSize: '14px', color: '#242424', fontWeight: '500' }}>
                                        {getRegionDisplay(details)} 
                                    </div>
                                </div>

                                {details.requestType && (
                                    <div>
                                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Request Type</div>
                                        <div style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '3px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            backgroundColor: getRequestTypeColor(details.requestType) + '15',
                                            color: getRequestTypeColor(details.requestType)
                                        }}>
                                            {getRequestTypeLabel(details.requestType)}
                                        </div>
                                    </div>
                                )}
                                
                                {details.passType && (
                                    <div>
                                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Pass Type</div>
                                        <div style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '3px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            backgroundColor: getPassTypeColor(details.passType) + '15',
                                            color: getPassTypeColor(details.passType)
                                        }}>
                                            {getPassTypeLabel(details.passType)}
                                        </div>
                                    </div>
                                )}

                                {details.startDate && (
                                    <div>
                                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Start Date</div>
                                        <div style={{ fontSize: '14px', color: '#242424', fontWeight: '500' }}>{formatDateOnly(details.startDate)}</div>
                                    </div>
                                )}
                                
                                {details.endDate && (
                                    <div>
                                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>End Date</div>
                                        <div style={{ fontSize: '14px', color: '#242424', fontWeight: '500' }}>{formatDateOnly(details.endDate)}</div>
                                    </div>
                                )}

                                {details.startDate && details.endDate && (
                                    <div style={{ gridColumn: '1 / -1' }}>
                                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Duration</div>
                                        <div style={{ fontSize: '14px', color: '#242424', fontWeight: '500' }}>{calculateDuration(details.startDate, details.endDate)}</div>
                                    </div>
                                )}

                                {details.contractNumber && (
                                    <div>
                                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Contract Number</div>
                                        <div style={{ fontSize: '14px', color: '#242424', fontWeight: '500' }}>{details.contractNumber}</div>
                                    </div>
                                )}
                                
                                {details.passIdentificationName && (
                                    <div>
                                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Pass ID Name</div>
                                        <div style={{ fontSize: '14px', color: '#242424', fontWeight: '500' }}>{details.passIdentificationName}</div>
                                    </div>
                                )}

                            
                            </div>
                        </div>

                        {/* Parent Request Info (Supplementary only) */}
                        {details.requestType === 'supplementary' && (details as any).parentRequestId && (
                            <div style={{ marginBottom: '24px' }}>
                                <h3 style={{
                                    margin: '0 0 16px 0',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    color: '#374151'
                                }}>
                                    Parent Request
                                </h3>
                                <div
                                    onClick={() => {
                                        setParentPopupRequestId((details as any).parentRequestId);
                                        setShowParentPopup(true);
                                    }}
                                    style={{
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                        backgroundColor: '#ffffff',
                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease'
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.borderColor = '#242424'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '12px 16px',
                                        backgroundColor: '#f8f9fa',
                                        borderBottom: '1px solid #f1f5f9'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#242424' }}>
                                                #{(details as any).parentRequestId}
                                            </span>
                                            <span style={{
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                color: '#374151',
                                                backgroundColor: '#f3f4f6',
                                                padding: '2px 8px',
                                                borderRadius: '4px'
                                            }}>
                                                Approved
                                            </span>
                                        </div>
                                        <span style={{ fontSize: '12px', color: '#242424', fontWeight: 500 }}>
                                            View Details →
                                        </span>
                                    </div>
                                    <div style={{ padding: '12px 16px', fontSize: '14px', color: '#64748b' }}>
                                        This is a supplementary request linked to parent request <span style={{ color: '#242424', fontWeight: 600 }}>#{(details as any).parentRequestId}</span>.
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Consolidated Access Code - shown when at Security stage or Fully Approved */}
                        {details.workflow && 
                         (details.workflow.currentState === 'SECURITY_APPROVER_PENDING' || 
                          details.workflow.currentState === 'FULLY_APPROVED') && (() => {
                            // Use backend value if available, otherwise compute from gates
                            let consolidatedCode = (details as any).consolidatedAccessCode || '';
                            if (!consolidatedCode && details.gates && details.gates.length > 0) {
                                const allCodes = new Set<string>();
                                details.gates.forEach((gate: any) => {
                                    if (gate.accessCodes && !gate.isExcluded) {
                                        gate.accessCodes.split(',').map((c: string) => c.trim()).filter((c: string) => c.length > 0).forEach((code: string) => allCodes.add(code));
                                    }
                                });
                                const sorted = [...allCodes].sort((a, b) => {
                                    const numA = Number(a), numB = Number(b);
                                    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                                    return a.localeCompare(b);
                                });
                                consolidatedCode = sorted.join(',');
                            }
                            if (!consolidatedCode) return null;
                            return (
                                <div style={{ marginBottom: '24px' }}>
                                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Consolidated Access Code</div>
                                    <div style={{ fontSize: '14px', color: '#242424', fontWeight: '500' }}>{consolidatedCode}</div>
                                </div>
                            );
                        })()}

                        {/* Gates Section */}
                        {details.gates && details.gates.length > 0 && (
                            <div style={{ marginBottom: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0', overflowX: 'auto' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <h3 style={{
                                        margin: '0',
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        color: '#242424'
                                    }}>
                                        Gates ({details.gates.length})
                                    </h3>
                                    
                                    {canExclude && (
                                        <button 
                                            onClick={() => toggleGateRemovalMode(task.taskId)}
                                            style={{
                                                padding: '6px 12px',
                                                backgroundColor: showGateRemovalMode[task.taskId] ? '#242424' : '#64748b',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '12px',
                                                fontWeight: '500'
                                            }}
                                        >
                                            {showGateRemovalMode[task.taskId] ? 'Done' : '➖ Edit Gates'}
                                        </button>
                                    )}
                                </div>

                                {canExclude && showGateRemovalMode[task.taskId] && (() => {
                                    const isGateStage = task.taskType === 'GATE_APPROVER' || task.taskType === 'GATE_REVIEWER';
                                    const isGateApprover = task.taskType === 'GATE_APPROVER';
                                    const hasBackendExcluded = details.gates.some(g => g.isExcluded);
                                    return (
                                        <div style={{
                                            backgroundColor: '#fef5f3',
                                            color: '#242424',
                                            padding: '12px',
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            marginBottom: '16px'
                                        }}>
                                            {isGateStage ? (
                                                <>
                                                    <strong>🚨 Edit Mode:</strong> Click on your assigned gate to exclude it. Non-assigned gates are read-only.
                                                    {isGateApprover && hasBackendExcluded && (
                                                        <span> You can also <strong>re-include</strong> previously excluded gates.</span>
                                                    )}
                                                </>
                                            ) : (
                                                <><strong>🚨 Removal Mode:</strong> Click on any gate to exclude it from approval.</>
                                            )}
                                        </div>
                                    );
                                })()}

                                {groupedGateNumbers.length > 0 && (
                                    <div style={{
                                        backgroundColor: '#fef5f3',
                                        border: '1px solid #F3694A30',
                                        borderRadius: '6px',
                                        padding: '10px 14px',
                                        marginBottom: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '13px',
                                        color: '#242424'
                                    }}>
                                        <span style={{ fontSize: '14px' }}>📍</span>
                                        <span>
                                            You are assigned to review{' '}
                                            <strong>
                                                {groupedGateNumbers.length === 1
                                                    ? `Gate ${groupedGateNumbers[0]}`
                                                    : `Gates ${groupedGateNumbers.join(', ')}`}
                                            </strong>
                                            {' '}— highlighted below.
                                        </span>
                                    </div>
                                )}
                                
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Gate Number</th>
                                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Location</th>
                                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Access Code</th>
                                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Status</th>
                                            <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Approval Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {details.gates.map((gate, index) => {
                                            const isExcludedInUI = canExclude && (excludedGates[task.taskId] || []).includes(gate.gateId);
                                            const isExcludedInBackend = gate.isExcluded;
                                            const isReincluded = (reincludedGates[task.taskId] || []).includes(gate.gateId);
                                            const isAssignedGate = groupedGateNumbers.length > 0 && groupedGateNumbers.includes(gate.gateNumber);
                                            const isGateStage = task.taskType === 'GATE_APPROVER' || task.taskType === 'GATE_REVIEWER';
                                            const isGateApprover = task.taskType === 'GATE_APPROVER';
                                            const isInEditMode = showGateRemovalMode[task.taskId];
                                            
                                            // Clickability rules:
                                            // 1. Backend-excluded + GATE_APPROVER + edit mode → clickable to re-include
                                            // 2. Gate stages (not backend-excluded): only assigned gate clickable
                                            // 3. Other stages (not backend-excluded): any gate clickable
                                            let isClickable = false;
                                            if (canExclude && isInEditMode) {
                                                if (isExcludedInBackend) {
                                                    // Only Gate Approver can reverse previous exclusions
                                                    isClickable = isGateApprover;
                                                } else {
                                                    // Normal exclusion logic
                                                    isClickable = isGateStage ? !!isAssignedGate : true;
                                                }
                                            }

                                            // Determine the effective visual state
                                            const effectivelyExcluded = isExcludedInBackend && !isReincluded;
                                            const effectivelyReincluded = isExcludedInBackend && isReincluded;
                                            
                                            // Click handler
                                            const handleGateClick = () => {
                                                if (!isClickable) return;
                                                if (isExcludedInBackend) {
                                                    toggleGateReinclude(task.taskId, gate.gateId);
                                                } else {
                                                    toggleGateExclusion(task.taskId, gate.gateId);
                                                }
                                            };
                                            
                                            return (
                                                <tr 
                                                    key={gate.gateId}
                                                    onClick={handleGateClick}
                                                    style={{
                                                        borderBottom: index < (details.gates?.length ?? 0) - 1 ? '1px solid #f1f5f9' : 'none',
                                                        backgroundColor: isExcludedInUI 
                                                            ? '#f9f9f9' 
                                                            : effectivelyReincluded 
                                                                ? '#f8f9fa' 
                                                                : effectivelyExcluded 
                                                                    ? '#f9fafb' 
                                                                    : isAssignedGate 
                                                                        ? '#fef5f3' 
                                                                        : (isClickable ? '#f8fafc' : 'white'),
                                                        cursor: isClickable ? 'pointer' : 'default',
                                                        opacity: (isExcludedInUI || effectivelyExcluded) ? 0.7 : 1,
                                                        borderLeft: isExcludedInUI 
                                                            ? '4px solid #F3694A' 
                                                            : effectivelyReincluded 
                                                                ? '4px solid #374151' 
                                                                : isAssignedGate && !effectivelyExcluded
                                                                    ? '4px solid #F3694A' 
                                                                    : '4px solid transparent',
                                                    }}
                                                >
                                                    <td style={{ padding: '12px 16px', fontWeight: '500', color: isExcludedInUI ? '#9ca3af' : effectivelyExcluded ? '#9ca3af' : '#242424', fontSize: '14px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span style={{ textDecoration: (isExcludedInUI || effectivelyExcluded) && !effectivelyReincluded ? 'line-through' : 'none' }}>
                                                                {gate.gateNumber}
                                                            </span>
                                                            {isAssignedGate && !isExcludedInUI && !effectivelyExcluded && (
                                                                <span style={{
                                                                    backgroundColor: '#F3694A',
                                                                    color: 'white',
                                                                    padding: '2px 6px',
                                                                    borderRadius: '4px',
                                                                    fontSize: '10px',
                                                                    fontWeight: '700',
                                                                    letterSpacing: '0.05em',
                                                                    textTransform: 'uppercase',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    YOUR REVIEW
                                                                </span>
                                                            )}
                                                            {/* Show READ ONLY badge only in gate stages for non-assigned, non-backend-excluded gates */}
                                                            {isGateStage && !isAssignedGate && isInEditMode && !isExcludedInBackend && (
                                                                <span style={{
                                                                    backgroundColor: '#f1f5f9',
                                                                    color: '#94a3b8',
                                                                    padding: '2px 6px',
                                                                    borderRadius: '4px',
                                                                    fontSize: '10px',
                                                                    fontWeight: '500',
                                                                    letterSpacing: '0.03em',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    READ ONLY
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '12px 16px', color: (isExcludedInUI || effectivelyExcluded) && !effectivelyReincluded ? '#d1d5db' : '#64748b', fontSize: '14px' }}>
                                                        {gate.locationName}
                                                    </td>
                                                    <td style={{ padding: '12px 16px', color: (isExcludedInUI || effectivelyExcluded) && !effectivelyReincluded ? '#d1d5db' : '#64748b', fontSize: '13px' }}>
                                                        {(gate as any).accessCodes ? (
                                                            <span style={{
                                                                backgroundColor: '#f3f4f6',
                                                                color: '#242424',
                                                                padding: '2px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '12px',
                                                                fontWeight: '500',
                                                                fontFamily: 'monospace'
                                                            }}>
                                                                {(gate as any).accessCodes}
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                                                        <span style={{
                                                            backgroundColor: gate.isActive ? '#f3f4f6' : '#f9f9f9',
                                                            color: gate.isActive ? '#374151' : '#F3694A',
                                                            padding: '2px 8px',
                                                            borderRadius: '6px',
                                                            fontSize: '11px',
                                                            fontWeight: '500'
                                                        }}>
                                                            {gate.isActive ? 'ACTIVE' : 'INACTIVE'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px' }}>
                                                        {effectivelyReincluded ? (
                                                            <span style={{
                                                                backgroundColor: '#f3f4f6',
                                                                color: '#374151',
                                                                padding: '2px 8px',
                                                                borderRadius: '6px',
                                                                fontSize: '11px',
                                                                fontWeight: '600'
                                                            }}>
                                                                ↩ TO BE RE-INCLUDED
                                                            </span>
                                                        ) : effectivelyExcluded && (gate as any).isCancelledByUser ? (
                                                            <span style={{
                                                                backgroundColor: '#fef5f3',
                                                                color: '#242424',
                                                                padding: '2px 8px',
                                                                borderRadius: '6px',
                                                                fontSize: '11px',
                                                                fontWeight: '600'
                                                            }}>
                                                                ⚡ SKIPPED BY USER
                                                            </span>
                                                        ) : effectivelyExcluded ? (
                                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                                                <span style={{
                                                                    backgroundColor: gate.exclusionReason && gate.exclusionReason.startsWith('Rejected by') ? '#f9f9f9' : '#f3f4f6',
                                                                    color: gate.exclusionReason && gate.exclusionReason.startsWith('Rejected by') ? '#F3694A' : '#374151',
                                                                    padding: '2px 8px',
                                                                    borderRadius: '6px',
                                                                    fontSize: '11px',
                                                                    fontWeight: gate.exclusionReason && gate.exclusionReason.startsWith('Rejected by') ? '600' : '500'
                                                                }}>
                                                                    {gate.exclusionReason && gate.exclusionReason.startsWith('Rejected by') ? '❌ REJECTED' : 'PREVIOUSLY EXCLUDED'}
                                                                </span>
                                                                {isGateApprover && isInEditMode && (
                                                                    <span style={{
                                                                        fontSize: '10px',
                                                                        color: '#6b7280',
                                                                        fontWeight: '500'
                                                                    }}>
                                                                        click to re-include
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ) : isExcludedInUI ? (
                                                            <span style={{
                                                                backgroundColor: '#f9f9f9',
                                                                color: '#242424',
                                                                padding: '2px 8px',
                                                                borderRadius: '6px',
                                                                fontSize: '11px',
                                                                fontWeight: '500'
                                                            }}>
                                                                TO BE EXCLUDED
                                                            </span>
                                                        ) : isClickable ? (
                                                            <span style={{
                                                                backgroundColor: '#fef5f3',
                                                                color: '#F3694A',
                                                                padding: '2px 8px',
                                                                borderRadius: '6px',
                                                                fontSize: '11px',
                                                                fontWeight: '500'
                                                            }}>
                                                                CLICK TO EXCLUDE
                                                            </span>
                                                        ) : (
                                                            <span style={{
                                                                backgroundColor: '#f3f4f6',
                                                                color: '#374151',
                                                                padding: '2px 8px',
                                                                borderRadius: '6px',
                                                                fontSize: '11px',
                                                                fontWeight: '500'
                                                            }}>
                                                                INCLUDED
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Personnel Section */}
                        {details.personnel && details.personnel.length > 0 && (
                            <div style={{ marginBottom: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0', overflowX: 'auto' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <h3 style={{
                                        margin: '0',
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        color: '#242424'
                                    }}>
                                        Personnel ({details.personnel.length})
                                    </h3>
                                    
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        {/* Show Badges button — only at Security Approval stage */}
                                        {task.taskType === 'SECURITY_APPROVER' && (
                                            <button
                                                onClick={() => handleShowBadges(details.personnel || [], task.gatePassId)}
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: '#242424',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '5px'
                                                }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="2" y="3" width="20" height="18" rx="2" />
                                                    <circle cx="9" cy="10" r="2.5" />
                                                    <path d="M15 8h4M15 12h4M5 16h14" />
                                                </svg>
                                                Show Badges
                                            </button>
                                        )}

                                        {canExclude && (
                                            <button 
                                                onClick={() => togglePersonnelRemovalMode(task.taskId)}
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: showPersonnelRemovalMode[task.taskId] ? '#242424' : '#64748b',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    fontWeight: '500'
                                                }}
                                            >
                                                {showPersonnelRemovalMode[task.taskId] ? 'Done' : '➖ Remove Personnel'}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {canExclude && showPersonnelRemovalMode[task.taskId] && (
                                    <div style={{
                                        backgroundColor: '#fef5f3',
                                        color: '#242424',
                                        padding: '12px',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        marginBottom: '16px'
                                    }}>
                                        <strong>🚨 Removal Mode:</strong> Click on personnel to exclude them from approval.
                                    </div>
                                )}
                                
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                            <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px', textTransform: 'uppercase' }}>NAME</th>
                                            <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px', textTransform: 'uppercase' }}>ID</th>
                                            <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px', textTransform: 'uppercase' }}>NATIONALITY</th>
                                            <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>PASSPORT EXPIRY</th>
                                            <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>VISA EXPIRY</th>
                                            <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '12px', textTransform: 'uppercase' }}>STATUS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {details.personnel.map((person, index) => {
                                            const personAny = person as any;
                                            const passportExpiry = personAny.passportExpiryDate || personAny.passportExpiry;
                                            const visaExpiry = person.visaExpiry;
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            const isPassportExpired = passportExpiry ? new Date(passportExpiry) < today : false;
                                            const isVisaExpiredVal = visaExpiry ? new Date(visaExpiry) < today : false;
                                            const isExcludedInUI = canExclude && (excludedPersonnel[task.taskId] || []).includes(person.personnelId);
                                            const isExcludedInBackend = person.isExcluded;
                                            const isExcluded = isExcludedInUI || isExcludedInBackend;
                                            const isClickable = canExclude && showPersonnelRemovalMode[task.taskId] && !isExcludedInBackend;
                                            const personName = personAny.fullName || `${personAny.firstName || ''} ${personAny.lastName || ''}`.trim();
                                            
                                            return (
                                                <tr 
                                                    key={person.gatePassPersonnelId}
                                                    onClick={() => isClickable ? togglePersonnelExclusion(task.taskId, person.personnelId) : undefined}
                                                    style={{
                                                        borderBottom: index < (details.personnel?.length ?? 0) - 1 ? '1px solid #f1f5f9' : 'none',
                                                        backgroundColor: isExcluded ? '#f9f9f9' : (isClickable ? '#f8fafc' : 'white'),
                                                        cursor: isClickable ? 'pointer' : 'default',
                                                        opacity: isExcluded ? 0.7 : 1
                                                    }}
                                                >
                                                    <td style={{ padding: '10px 8px', fontWeight: '600', fontSize: '14px' }}>
                                                       <button
    type="button"
    onClick={(e) => {
        e.stopPropagation();
        handleViewPersonnel(person.personnelId);
    }}
    style={{
        color: personName ? '#1f2937' : '#242424',
        textDecoration: 'none',
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit',
        fontWeight: 'inherit',
        fontSize: 'inherit'
    }}
    onMouseEnter={(e) => { e.currentTarget.style.color = '#242424'; e.currentTarget.style.textDecoration = 'underline'; }}
    onMouseLeave={(e) => { e.currentTarget.style.color = personName ? '#1f2937' : '#242424'; e.currentTarget.style.textDecoration = 'none'; }}
    title="View personnel details"
>
    {personName || person.nationalIdNo || 'View Details'}
</button>
                                                    </td>
                                                    <td style={{ padding: '10px 8px', color: '#64748b', fontSize: '13px' }}>
                                                        {person.nationalIdNo ? 
                                                            `${person.nationalIdNo.substring(0, 2)}${'*'.repeat(Math.max(0, person.nationalIdNo.length - 2))}` 
                                                            : 'N/A'
                                                        }
                                                    </td>
                                                    <td style={{ padding: '10px 8px', color: '#64748b', fontSize: '14px' }}>
                                                        {person.nationality || '—'}
                                                    </td>
                                                    <td style={{ padding: '10px 8px', fontSize: '13px' }}>
                                                        {passportExpiry ? (
                                                            <div style={{ color: isPassportExpired ? '#F3694A' : '#374151', fontWeight: isPassportExpired ? 500 : 400 }}>
                                                                {new Date(passportExpiry).toLocaleDateString()}
                                                                {isPassportExpired && (
                                                                    <div style={{
                                                                        display: 'inline-block',
                                                                        marginLeft: '0',
                                                                        marginTop: '4px',
                                                                        padding: '1px 6px',
                                                                        borderRadius: '3px',
                                                                        fontSize: '9px',
                                                                        fontWeight: 700,
                                                                        textTransform: 'uppercase' as const,
                                                                        background: '#fef5f3',
                                                                        color: '#F3694A',
                                                                        border: '1.5px solid #242424'
                                                                    }}>Expired</div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '10px 8px', fontSize: '13px' }}>
                                                        {visaExpiry ? (
                                                            <div style={{ color: isVisaExpiredVal ? '#F3694A' : '#374151', fontWeight: isVisaExpiredVal ? 500 : 400 }}>
                                                                {new Date(visaExpiry).toLocaleDateString()}
                                                                {isVisaExpiredVal && (
                                                                    <div style={{
                                                                        display: 'inline-block',
                                                                        marginLeft: '0',
                                                                        marginTop: '4px',
                                                                        padding: '1px 6px',
                                                                        borderRadius: '3px',
                                                                        fontSize: '9px',
                                                                        fontWeight: 700,
                                                                        textTransform: 'uppercase' as const,
                                                                        background: '#fef5f3',
                                                                        color: '#F3694A',
                                                                        border: '1.5px solid #242424'
                                                                    }}>Expired</div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No Visa</span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: '14px' }}>
                                                        {isExcludedInBackend ? (
                                                            <span style={{
                                                                backgroundColor: '#f3f4f6',
                                                                color: '#374151',
                                                                padding: '2px 8px',
                                                                borderRadius: '6px',
                                                                fontSize: '11px',
                                                                fontWeight: '500'
                                                            }}>
                                                                PREVIOUSLY EXCLUDED
                                                            </span>
                                                        ) : isExcludedInUI ? (
                                                            <span style={{
                                                                backgroundColor: '#f9f9f9',
                                                                color: '#242424',
                                                                padding: '2px 8px',
                                                                borderRadius: '6px',
                                                                fontSize: '11px',
                                                                fontWeight: '500'
                                                            }}>
                                                                TO BE EXCLUDED
                                                            </span>
                                                        ) : isClickable ? (
                                                            <span style={{
                                                                backgroundColor: '#f3f4f6',
                                                                color: '#374151',
                                                                padding: '2px 8px',
                                                                borderRadius: '6px',
                                                                fontSize: '11px',
                                                                fontWeight: '500'
                                                            }}>
                                                                ACTIVE
                                                            </span>
                                                        ) : (
                                                            <span style={{
                                                                backgroundColor: '#f3f4f6',
                                                                color: '#374151',
                                                                padding: '2px 8px',
                                                                borderRadius: '6px',
                                                                fontSize: '11px',
                                                                fontWeight: '500'
                                                            }}>
                                                                APPROVED
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Workflow Information Section */}
                        {details.workflow && (
                            <div style={{ marginBottom: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
                                <h3 style={{
                                    margin: '0 0 16px 0',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    color: '#242424'
                                }}>
                                    Workflow Status
                                </h3>
                                
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '16px 32px',
                                    marginBottom: '20px'
                                }}>
                                    <div>
                                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Current Status</div>
                                        <div style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            backgroundColor: '#f3f4f6',
                                            color: '#242424',
                                            border: '1px solid #e2e8f0'
                                        }}>
                                            {getWorkflowStateDisplay(details.workflow.currentState).label}
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Overall Progress</div>
                                        {renderProgressBar(details.workflow.workflowProgress, details.workflow.currentState)}
                                    </div>
                                    
                                    <div>
                                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Last Updated</div>
                                        <div style={{ fontSize: '14px', color: '#242424', fontWeight: '500' }}>
                                            {formatDateOnly(details.workflow.stateChangedAt)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Task Actions Section */}
                        <div className="task-actions-wrapper">
                            <h3 style={{
                                margin: '0 0 16px 0',
                                fontSize: '15px',
                                fontWeight: '600',
                                color: '#242424'
                            }}>
                                Task Actions
                            </h3>

                            {/* ── Contextual Stage Guidance — positioned in right panel space ── */}
                            <div className="task-actions-context-guide">
                                <div className="task-actions-context-inner">
                                    <div className="task-help-stage-header">
                                        <span style={{ fontSize: '14px' }}>{stageInfo.icon}</span>
                                        <span className="task-help-stage-label">{stageInfo.label}</span>
                                    </div>
                                    <div className="task-help-stage-body">
                                        {task.taskType === 'COMPANY_APPROVER' ? (() => {
                                            const approverCompany = getUserInfo().companyName;
                                            const submitterCompany = details?.companyName || task.companyName;
                                            const isSameCompany = approverCompany && submitterCompany && approverCompany.toLowerCase().trim() === submitterCompany.toLowerCase().trim();
                                            return (
                                                <>
                                                    <p style={{ margin: '0 0 8px 0' }}>
                                                        {isSameCompany
                                                            ? 'This request has been raised from within your organisation.'
                                                            : `This request has been submitted by ${submitterCompany} and requires your company's approval.`
                                                        }
                                                    </p>
                                                    <p style={{ margin: 0 }}>
                                                        Your approval is needed to advance this request. Verify the details align with contract requirements. You may reject or return for revision if needed.
                                                    </p>
                                                </>
                                            );
                                        })() : task.taskType === 'COMPANY_REVIEWER' ? (() => {
                                            const reviewerCompany = getUserInfo().companyName;
                                            const submitterCompany = details?.companyName || task.companyName;
                                            const isSameCompany = reviewerCompany && submitterCompany && reviewerCompany.toLowerCase().trim() === submitterCompany.toLowerCase().trim();
                                            const crAuthStatus = regionAuthStatus[task.gatePassId];
                                            const crIsAuthLoading = crAuthStatus?.loading ?? true;
                                            const crIsAuthorized = crAuthStatus ? crAuthStatus.isAuthorized : false;

                                            if (crIsAuthLoading) {
                                                return (
                                                    <p style={{ margin: 0, color: '#64748b' }}>
                                                        Checking company authorisation for this region…
                                                    </p>
                                                );
                                            }

                                            return (
                                                <div>
                                                    <p style={{ margin: '0 0 8px 0' }}>
                                                        This request has been sent to you for review.
                                                        {isSameCompany
                                                            ? ' It was raised from within your organisation.'
                                                            : ` It was submitted by ${submitterCompany}.`
                                                        }
                                                    </p>

                                                    {crIsAuthorized ? (
                                                        <>
                                                            <p style={{ margin: '0 0 4px 0', fontWeight: 500, color: '#374151' }}>
                                                                Your company is an authorised partner in this region.
                                                            </p>
                                                            <p style={{ margin: '0 0 6px 0' }}>Forward this request using the options on the left:</p>
                                                            <ul style={{ margin: '0', paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                <li><span style={{ fontWeight: 500, color: '#374151' }}>Send for Access Area Approval</span> <span style={{ fontSize: '10px', color: '#F3694A', fontWeight: 600 }}>(required)</span> — select an approver from your company.</li>
                                                                <li><span style={{ fontWeight: 500, color: '#374151' }}>Send to Another Company</span> <span style={{ fontSize: '10px', color: '#6b7280' }}>(optional)</span> — forward for additional review.</li>
                                                            </ul>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <p style={{ margin: '0 0 4px 0', fontWeight: 500, color: '#374151' }}>
                                                                Your company is not authorised in this region.
                                                            </p>
                                                            <p style={{ margin: '0 0 6px 0' }}>Complete both steps on the left:</p>
                                                            <ul style={{ margin: '0', paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                <li><span style={{ fontWeight: 500, color: '#374151' }}>Send to Another Company</span> <span style={{ fontSize: '10px', color: '#F3694A', fontWeight: 600 }}>(required)</span> — forward to the contracting company.</li>
                                                                <li><span style={{ fontWeight: 500, color: '#374151' }}>Send for Approval</span> <span style={{ fontSize: '10px', color: '#F3694A', fontWeight: 600 }}>(required)</span> — select an approver from your company.</li>
                                                            </ul>
                                                        </>
                                                    )}

                                                    <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#9ca3af' }}>
                                                        You may also reject the request or return it for revision using the options at the bottom.
                                                    </p>
                                                </div>
                                            );
                                        })() : task.taskType === 'GATE_REVIEWER' ? (() => {
                                            const assignedGates = details?.gates
                                                ? details.gates.filter((g: any) => groupedGateNumbers.includes(g.gateNumber) && !g.isExcluded)
                                                : [];
                                            const accessCodes = new Set<string>();
                                            assignedGates.forEach((gate: any) => {
                                                if (gate.accessCodes) {
                                                    gate.accessCodes.split(',').map((c: string) => c.trim()).filter((c: string) => c.length > 0).forEach((code: string) => accessCodes.add(code));
                                                }
                                            });
                                            const sortedCodes = [...accessCodes].sort((a, b) => {
                                                const numA = Number(a), numB = Number(b);
                                                if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                                                return a.localeCompare(b);
                                            });
                                            const gateLabel = groupedGateNumbers.length === 1
                                                ? `Gate ${groupedGateNumbers[0]}`
                                                : `Gates ${groupedGateNumbers.join(', ')}`;

                                            return (
                                                <div>
                                                    <p style={{ margin: '0 0 8px 0' }}>
                                                        This request requires your endorsement of the access area(s) listed below.
                                                    </p>
                                                    {sortedCodes.length > 0 && (
                                                        <div className="task-help-access-codes">
                                                            <span style={{ fontSize: '11px', color: '#6b7280' }}>Access Codes ({gateLabel}):</span>
                                                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#242424', fontFamily: 'monospace' }}>
                                                                {sortedCodes.join(', ')}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <p style={{ margin: '8px 0 4px 0', fontWeight: 500, color: '#374151' }}>Your action:</p>
                                                    <ul style={{ margin: '0', paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                        <li><strong>Endorse</strong> — choose Endorse Request for Access Area</li>
                                                        <li><strong>Reject</strong> — choose Reject and provide a reason</li>
                                                    </ul>
                                                </div>
                                            );
                                        })() : task.taskType === 'GATE_APPROVER' ? (() => {
                                            const assignedGates = details?.gates
                                                ? details.gates.filter((g: any) => groupedGateNumbers.includes(g.gateNumber) && !g.isExcluded)
                                                : [];
                                            const accessCodes = new Set<string>();
                                            assignedGates.forEach((gate: any) => {
                                                if (gate.accessCodes) {
                                                    gate.accessCodes.split(',').map((c: string) => c.trim()).filter((c: string) => c.length > 0).forEach((code: string) => accessCodes.add(code));
                                                }
                                            });
                                            const sortedCodes = [...accessCodes].sort((a, b) => {
                                                const numA = Number(a), numB = Number(b);
                                                if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                                                return a.localeCompare(b);
                                            });
                                            const gateLabel = groupedGateNumbers.length === 1
                                                ? `Gate ${groupedGateNumbers[0]}`
                                                : `Gates ${groupedGateNumbers.join(', ')}`;

                                            return (
                                                <div>
                                                    <p style={{ margin: '0 0 8px 0' }}>
                                                        This request requires your approval of the access area(s) shown below.
                                                    </p>
                                                    {sortedCodes.length > 0 && (
                                                        <div className="task-help-access-codes">
                                                            <span style={{ fontSize: '11px', color: '#6b7280' }}>Access Codes ({gateLabel}):</span>
                                                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#242424', fontFamily: 'monospace' }}>
                                                                {sortedCodes.join(', ')}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <p style={{ margin: '8px 0 4px 0', fontWeight: 500, color: '#374151' }}>Your action:</p>
                                                    <ul style={{ margin: '0', paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                        <li><strong>Approve</strong> — choose Endorse Request for Access Area</li>
                                                        <li><strong>Reject</strong> — choose Reject and provide a reason</li>
                                                    </ul>
                                                </div>
                                            );
                                        })() : task.taskType === 'SECURITY_APPROVER' ? (
                                            <p style={{ margin: 0 }}>
                                                All gate approvals are complete. Review the consolidated access codes and personnel list, then provide your security approval to finalize and issue the gate pass.
                                            </p>
                                        ) : (
                                            <p style={{ margin: 0 }}>{stageInfo.explanation}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Company Reviewer Additional Selections */}
                            {/* Company Reviewer Additional Selections */}
{task.taskType === 'COMPANY_REVIEWER' && (() => {
    // ---------------------------------------------------------
    // 1. REGION-SPECIFIC AUTHORIZATION CHECK
    // ---------------------------------------------------------
    
    // UPDATED: We now check if the company is authorized for the specific
    // region of the gate pass request (via the /companies/{id}/authorized/{regionId} API).
    // This replaces the old company-level flag (details.isCompanyAuthorized).
    const authStatus = regionAuthStatus[task.gatePassId];
    const isAuthLoading = authStatus?.loading ?? true;
    const isAuthorizedCompany = authStatus ? authStatus.isAuthorized : false;

    console.log('Authorization Debug (Region-Specific):', { 
        reviewerCompany: getUserInfo().companyName,
        reviewerCompanyId: getUserInfo().companyId,
        submitterCompany: details.companyName, 
        regionId: details.regionId || (details as any).region,
        isAuthorized: isAuthorizedCompany,
        isLoading: isAuthLoading,
        source: 'region-auth-api (checks reviewer company, not submitter)'
    });

    // ---------------------------------------------------------
    // 2. LOGIC & STATE
    // ---------------------------------------------------------
    const currentApprovers = additionalApproverSelection[task.taskId]?.selectedApprovers || [];
    const currentReviewers = additionalReviewerSelection[task.taskId] && 
                             hasSelectedReviewers(additionalReviewerSelection[task.taskId]) 
                             ? getSelectedReviewers(additionalReviewerSelection[task.taskId]) 
                             : [];

    const isApproverSelected = currentApprovers.length > 0;
    
    // Reviewer is mandatory ONLY if company is NOT authorized
    const isReviewerRequired = !isAuthorizedCompany; 
    const isReviewerSelected = currentReviewers.length > 0;

    // Approval Validation (also blocked while authorization check is loading)
    const canApprove = !isAuthLoading && isApproverSelected && (!isReviewerRequired || isReviewerSelected);

    // ---------------------------------------------------------
    // 3. UI RENDER
    // ---------------------------------------------------------
    return (
        <div style={{ 
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            marginBottom: '24px',
            border: '1px solid #f1f5f9',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        }}>
            {/* Header Section */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
               
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    
                    {isAuthLoading && (
                        <span style={{ 
                            fontSize: '11px', 
                            backgroundColor: '#f1f5f9', 
                            color: '#64748b', 
                            padding: '2px 8px', 
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0',
                            fontWeight: '500'
                        }}>
                            Checking authorisation…
                        </span>
                    )}
                </div>
            </div>
            
            
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '20px',
                    marginBottom: '24px'
                }}>
                    {/* 1. Approver Selection Card */}
                    <div style={{
                        border: isApproverSelected ? '1px solid #F3694A' : '1px solid #e2e8f0',
                        backgroundColor: isApproverSelected ? '#fef5f3' : '#ffffff',
                        borderRadius: '8px',
                        padding: '20px',
                        transition: 'all 0.2s ease',
                        position: 'relative'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', alignItems: 'flex-start' }}>
                            <div>
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>{isAuthorizedCompany ? 'Send for Access Area Approval' : 'Send for Approval'}</div>
                                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Select an approver from your company</div>
                            </div>
                            {isApproverSelected ? (
                                <div style={{ backgroundColor: '#F3694A', color: 'white', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>✓</div>
                            ) : (
                                <span style={{ fontSize: '11px', color: '#F3694A', fontWeight: '600', backgroundColor: '#fef5f3', padding: '2px 8px', borderRadius: '4px' }}>REQUIRED</span>
                            )}
                        </div>
                        
                        <div style={{ minHeight: '40px', marginBottom: '16px' }}>
                            {isApproverSelected ? (
                                <div style={{ fontSize: '13px', color: '#242424', fontWeight: '500' }}>
                                    {currentApprovers.length} approver(s) selected
                                </div>
                            ) : (
                                <div style={{ fontSize: '13px', color: '#94a3b8' }}>
                                    Select an approver from your company. Once approved, the request advances to Gate Review.
                                </div>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={() => setShowAdditionalApproverSelection(task.taskId)}
                            style={{
                                width: '100%',
                                padding: '10px',
                                backgroundColor: 'white',
                                border: isApproverSelected ? '1px solid #F3694A' : '1px solid #cbd5e1',
                                borderRadius: '6px',
                                color: isApproverSelected ? '#F3694A' : '#475569',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            {isApproverSelected ? 'Change Selection' : 'Select Approvers'}
                        </button>
                    </div>

                    {/* 2. Reviewer Selection Card */}
                    <div style={{
                        border: isReviewerSelected ? '1px solid #64748b' : (isReviewerRequired ? '1px solid #e2e8f0' : '1px dashed #e2e8f0'),
                        backgroundColor: isReviewerSelected ? '#f8fafc' : '#ffffff',
                        borderRadius: '8px',
                        padding: '20px',
                        transition: 'all 0.2s ease',
                        opacity: !isReviewerRequired && !isReviewerSelected ? 0.8 : 1
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', alignItems: 'flex-start' }}>
                            <div>
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>Send to Another Company</div>
                                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                    {isReviewerRequired ? 'Forward to another company for approval' : 'Optional — authorised company in this region'}
                                </div>
                            </div>
                             {isReviewerSelected ? (
                                <div style={{ backgroundColor: '#64748b', color: 'white', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>✓</div>
                            ) : isReviewerRequired ? (
                                <span style={{ fontSize: '11px', color: '#F3694A', fontWeight: '600', backgroundColor: '#fef5f3', padding: '2px 8px', borderRadius: '4px' }}>REQUIRED</span>
                            ) : (
                                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '500', backgroundColor: '#f1f5f9', padding: '2px 8px', borderRadius: '4px' }}>OPTIONAL</span>
                            )}
                        </div>

                        <div style={{ minHeight: '40px', marginBottom: '16px' }}>
                             {isReviewerSelected ? (
                                <div style={{ fontSize: '13px', color: '#475569', fontWeight: '500' }}>
                                    {currentReviewers.length} reviewer(s) selected from another company
                                </div>
                            ) : (
                                <div style={{ fontSize: '13px', color: '#94a3b8' }}>
                                    {isAuthLoading
                                        ? "Checking company authorisation for this region…"
                                        : isReviewerRequired 
                                            ? "Your company is not authorised in this region. You must forward this request to the contracting company or another authorised company for review."
                                            : "Your company is authorised in this region — forwarding to another company is optional."}
                                </div>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={() => setShowAdditionalReviewerSelection(task.taskId)}
                            style={{
                                width: '100%',
                                padding: '10px',
                                backgroundColor: 'white',
                                border: isReviewerSelected ? '1px solid #94a3b8' : '1px solid #cbd5e1',
                                borderRadius: '6px',
                                color: isReviewerSelected ? '#475569' : '#475569',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            {isReviewerSelected ? 'Change Selection' : 'Select Company'}
                        </button>
                    </div>
                </div>

                {/* Validation Banner */}
                {(isAuthLoading || !canApprove) && (
                    <div style={{
                        padding: '12px 16px',
                        backgroundColor: isAuthLoading ? '#fef5f3' : '#f9f9f9',
                        border: isAuthLoading ? '1px solid #F3694A30' : '1px solid #e2e8f0',
                        borderRadius: '6px',
                        color: isAuthLoading ? '#F3694A' : '#242424',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '20px'
                    }}>
                        <span>{isAuthLoading ? '⏳' : '⚠️'}</span>
                        <span>
                            {isAuthLoading ? (
                                <><strong>Checking authorisation…</strong> Please wait while we verify company authorisation for this region.</>
                            ) : (
                                <>
                                    <strong>Action Required:</strong> Please select
                                    {!isApproverSelected ? ' an Approver' : ''}
                                    {(!isApproverSelected && isReviewerRequired && !isReviewerSelected) ? ' and' : ''}
                                    {(isReviewerRequired && !isReviewerSelected) ? ' a Company' : ''}
                                    {' '}to proceed.
                                </>
                            )}
                        </span>
                    </div>
                )}

                {/* Action Bar inside COMPANY_REVIEWER block - Inline Comments */}
                <div style={{ marginTop: '24px' }}>
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                            Comments <span style={{ fontWeight: '400', color: '#94a3b8' }}>(optional)</span>
                        </label>
                        <textarea
                            value={taskComments[task.taskId] || ''}
                            onChange={(e) => setTaskComments({ ...taskComments, [task.taskId]: e.target.value })}
                            placeholder="Add any comments or notes for this action..."
                            rows={3}
                            style={{
                                width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '6px',
                                fontSize: '13px', color: '#334155', backgroundColor: '#f8fafc', resize: 'vertical',
                                outline: 'none', lineHeight: '1.5', fontFamily: 'inherit', boxSizing: 'border-box',
                                transition: 'border-color 0.2s ease'
                            }}
                            onFocus={(e) => e.currentTarget.style.borderColor = '#F3694A'}
                            onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                        <button 
                            onClick={() => processGroupedTask(selectedTaskForDetail!, 'APPROVED', taskComments[task.taskId] || '')}
                            disabled={processingTaskId === task.taskId || !canApprove}
                            style={{
                                padding: '10px 24px', backgroundColor: canApprove ? '#F3694A' : '#F3694A',
                                color: canApprove ? 'white' : 'white', border: '1px solid #F3694A',
                                borderRadius: '6px', cursor: canApprove ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: '500',
                                display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                opacity: canApprove ? 1 : 0.5
                            }}
                            onMouseOver={(e) => { if (canApprove) e.currentTarget.style.backgroundColor = '#E55A42'; }}
                            onMouseOut={(e) => { if (canApprove) e.currentTarget.style.backgroundColor = '#F3694A'; }}
                        >
                            {processingTaskId === task.taskId ? 'Processing...' : 'Approve & Proceed'}
                            {canApprove && <span>→</span>}
                        </button>
                        <button 
                            onClick={() => {
                                const comments = taskComments[task.taskId] || '';
                                if (!comments.trim()) {
                                    alert('Please provide comments explaining why you are sending this back');
                                    return;
                                }
                                if (window.confirm('Send this request back to the submitter for revisions?')) {
                                    processGroupedTask(selectedTaskForDetail!, 'RETURNED', comments);
                                }
                            }}
                            disabled={processingTaskId === task.taskId}
                            style={{
                                padding: '10px 20px', backgroundColor: '#f3f4f6', color: '#242424',
                                border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                                fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px'
                            }}
                        >
                            ↩️ Return for Revision
                        </button>
                        <button 
                            onClick={() => {
                                const comments = taskComments[task.taskId] || '';
                                if (!comments.trim()) {
                                    alert('Please provide comments explaining why you are rejecting this request');
                                    return;
                                }
                                if (window.confirm('Are you sure you want to reject this request? This action cannot be undone.')) {
                                    processGroupedTask(selectedTaskForDetail!, 'REJECTED', comments);
                                }
                            }}
                            disabled={processingTaskId === task.taskId}
                            style={{
                                padding: '10px 20px', backgroundColor: 'transparent', color: '#242424',
                                border: '1.5px solid #242424', borderRadius: '6px', cursor: 'pointer',
                                fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px'
                            }}
                        >
                            ✕ Reject Request
                        </button>
                    </div>
                </div>
            
        </div>
    );
})()}

                            {/* Action Buttons */}
                           {/* STANDARD ACTION BUTTONS */}
{/* Standard Action Buttons */}
{task.taskType !== 'COMPANY_REVIEWER' && (
    <div style={{ paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
        {/* Always-visible Comments Box */}
        <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                Comments <span style={{ fontWeight: '400', color: '#94a3b8' }}>(optional)</span>
            </label>
            <textarea
                value={taskComments[task.taskId] || ''}
                onChange={(e) => setTaskComments({ ...taskComments, [task.taskId]: e.target.value })}
                placeholder="Add any comments or notes for this action..."
                rows={3}
                style={{
                    width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '6px',
                    fontSize: '13px', color: '#334155', backgroundColor: '#f8fafc', resize: 'vertical',
                    outline: 'none', lineHeight: '1.5', fontFamily: 'inherit', boxSizing: 'border-box',
                    transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#F3694A'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
            />
        </div>

        {/* Action Buttons Row */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button 
                onClick={() => processGroupedTask(selectedTaskForDetail!, 'APPROVED', taskComments[task.taskId] || '')}
                disabled={processingTaskId === task.taskId}
                style={{
                    padding: '10px 24px', backgroundColor: '#F3694A', color: 'white',
                    border: '1px solid #F3694A', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center',
                    gap: '6px', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#E55A42'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#F3694A'}
            >
                {processingTaskId === task.taskId ? '⏳ Processing...' : ((task.taskType === 'GATE_REVIEWER' || task.taskType === 'GATE_APPROVER') ? 'Endorse Request for Access Area' : 'Approve Request')}
            </button>

            {/* Return for Revision Button */}
            {(task.taskType === 'COMPANY_APPROVER' || task.taskType === 'SECURITY_APPROVER') && (
                <button 
                    onClick={() => {
                        const comments = taskComments[task.taskId] || '';
                         if (!comments.trim()) {
                            alert('Please provide comments explaining why you are sending this back');
                            return;
                        }
                        if (window.confirm('Send this request back to the submitter for revisions?')) {
                            processGroupedTask(selectedTaskForDetail!, 'RETURNED', comments);
                        }
                    }}
                    disabled={processingTaskId === task.taskId}
                    style={{
                        padding: '10px 20px', backgroundColor: '#f3f4f6', color: '#242424',
                        border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px'
                    }}
                >
                    ↩️ Return for Revision
                </button>
            )}

            {/* Reject Request Button */}
            {(task.taskType === 'COMPANY_APPROVER' || task.taskType === 'SECURITY_APPROVER') && (
                <button 
                    onClick={() => {
                        const comments = taskComments[task.taskId] || '';
                        if (!comments.trim()) {
                            alert('Please provide comments explaining why you are rejecting this request');
                            return;
                        }
                        if (window.confirm('Are you sure you want to reject this request? This action cannot be undone.')) {
                            processGroupedTask(selectedTaskForDetail!, 'REJECTED', comments);
                        }
                    }}
                    disabled={processingTaskId === task.taskId}
                    style={{
                        padding: '10px 20px', backgroundColor: 'transparent', color: '#242424',
                        border: '1.5px solid #242424', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px'
                    }}
                >
                    ✕ Reject Request
                </button>
            )}

            {/* Reject Assigned Gates Button - for Gate Approver and Gate Reviewer */}
            {(task.taskType === 'GATE_APPROVER' || task.taskType === 'GATE_REVIEWER') && (
                <button 
                    onClick={() => {
                        const comments = taskComments[task.taskId] || '';
                        if (!comments.trim()) {
                            alert((task.taskType === 'GATE_REVIEWER' || task.taskType === 'GATE_APPROVER')
                                ? 'Please provide a reason for rejecting the access area request in the Comments field'
                                : 'Please provide comments explaining why you are rejecting the assigned gate(s)');
                            return;
                        }
                        const gateNames = selectedTaskForDetail!.assignedGateNumbers.join(', ');
                        const gateLabel = selectedTaskForDetail!.assignedGateNumbers.length === 1 ? 'Gate' : 'Gates';
                        if (window.confirm(
                            (task.taskType === 'GATE_REVIEWER' || task.taskType === 'GATE_APPROVER')
                                ? `Are you sure you want to reject the access area request for ${gateLabel} ${gateNames}?\n\nYour assigned access area(s) will be excluded from the pass. Other areas will continue their approval flow normally.`
                                : `Are you sure you want to reject ${gateLabel} ${gateNames}?\n\nOnly your assigned gate(s) will be rejected and excluded from the pass. Other gates will continue their approval flow normally.`
                        )) {
                            processGroupedTask(selectedTaskForDetail!, 'REJECTED', comments);
                        }
                    }}
                    disabled={processingTaskId === task.taskId}
                    style={{
                        padding: '10px 20px', backgroundColor: 'transparent', color: '#242424',
                        border: '1.5px solid #242424', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px'
                    }}
                >
                    ✕ {(task.taskType === 'GATE_REVIEWER' || task.taskType === 'GATE_APPROVER')
                        ? `Reject Request for Access Area${selectedTaskForDetail!.assignedGateNumbers.length > 1 ? 's' : ''}`
                        : `Reject Gate${selectedTaskForDetail!.assignedGateNumbers.length > 1 ? 's' : ''}`
                    }
                </button>
            )}
        </div>
    </div>
)}
                        </div>

                        {/* Activity Timeline — Enterprise Table */}
                        {logs && logs.length > 0 && (
                            <div style={{ paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
                                <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: 600, color: '#242424' }}>
                                    Activity Timeline ({logs.length})
                                </h3>
                                
                                <div className="companies-table-container" style={{ maxHeight: '450px', overflowY: 'auto', overflowX: 'auto' }}>
                                    <table className="minimalist-table" style={{ tableLayout: 'fixed', width: '100%', minWidth: '1100px' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '150px' }}>ACTION</th>
                                                <th style={{ width: '190px' }}>BY</th>
                                                <th style={{ width: '160px' }}>COMPANY</th>
                                                <th style={{ width: '170px' }}>ASSIGNED TO</th>
                                                <th style={{ width: '130px' }}>DATE</th>
                                                <th>COMMENTS</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logs.map((log, index) => {
                                                const logDetails = log.details ? (() => { try { return JSON.parse(log.details); } catch { return {}; } })() : {};

                                                const performerName = (log as any).performedByName || (log as any).displayName || logDetails.performedByName || logDetails.userName || '';
                                                const performerEmail = (log as any).performedByEmail || (log as any).displayEmail || (log as any).performedBy || '';
                                                const performerCompany = (log as any).performedByCompanyName || (log as any).displayCompanyName || logDetails.performerCompany || '';
                                                const assignedToName = (log as any).assignedToName || logDetails.assignedToName || logDetails.newUserName || '';
                                                const assignedToEmail = (log as any).assignedToEmail || logDetails.assignedToEmail || logDetails.newUserEmail || '';
                                                const comments = logDetails.comments || (log as any).notes || '';

                                                const stageType = (log as any).workflowStage || (log as any).stageType || (log as any).taskType 
                                                    || logDetails.workflowStage || logDetails.stageType || logDetails.taskType || '';
                                                const stageDisplayMap: Record<string, string> = {
                                                    'COMPANY_APPROVER': 'Company Approval', 'COMPANY_REVIEWER': 'Company Review',
                                                    'GATE_APPROVER': 'Gate Approval', 'GATE_REVIEWER': 'Gate Review',
                                                    'SECURITY_APPROVER': 'Security Approval', 'COMPANY_APPROVAL': 'Company Approval',
                                                    'COMPANY_REVIEW': 'Company Review', 'GATE_APPROVAL': 'Gate Approval',
                                                    'GATE_REVIEW': 'Gate Review', 'SECURITY_APPROVAL': 'Security Approval',
                                                };
                                                const stageName = stageDisplayMap[stageType] || '';
                                                const performerDisplay = performerName || performerEmail || formatPerformerInfo(log);
                                                const names = assignedToName ? assignedToName.split(', ').filter(Boolean) : [];
                                                const emails = assignedToEmail ? assignedToEmail.split(', ').filter(Boolean) : [];
                                                const assigneeItems = names.length > 0 ? names : emails;

                                                const decisionDot = logDetails.decision === 'APPROVED' ? '#242424' 
                                                    : logDetails.decision === 'REJECTED' ? '#242424' 
                                                    : logDetails.decision === 'RETURNED' ? '#F3694A' : null;

                                                return (
                                                    <tr key={log.auditId || index}>
                                                        {/* Action */}
                                                        <td style={{ overflow: 'hidden' }}>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    {decisionDot && (
                                                                        <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: decisionDot, flexShrink: 0 }} />
                                                                    )}
                                                                    <span style={{ fontWeight: 600, color: '#242424', fontSize: '13px' }}>
                                                                        {stageName || getActionLabel(log.actionType)}
                                                                    </span>
                                                                </div>
                                                                {stageName && (
                                                                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                                                                        {getActionLabel(log.actionType)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>

                                                        {/* By */}
<td style={{ overflow: 'hidden', maxWidth: '190px' }}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', overflow: 'hidden' }}>
        <span style={{ color: '#242424', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{performerDisplay || '—'}</span>
        {performerEmail && performerName && (
            <span style={{ fontSize: '11px', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{performerEmail}</span>
        )}
    </div>
</td>

                                                        {/* Company */}
                                                        <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><span style={{ color: '#6b7280' }}>{performerCompany || '—'}</span></td>

                                                        {/* Assigned To */}
                                                        <td style={{ overflow: 'hidden' }}>
                                                            {assigneeItems.length === 0 ? (
                                                                <span style={{ color: '#d1d5db' }}>—</span>
                                                            ) : assigneeItems.length === 1 ? (
                                                                <span style={{ color: '#242424' }}>{names[0] || emails[0]}</span>
                                                            ) : (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                    {assigneeItems.map((item: string, i: number) => (
                                                                        <span key={i} style={{ fontSize: '12px', color: '#242424' }}>
                                                                            {names[i] || emails[i] || item}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </td>

                                                        {/* Date */}
                                                        <td>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                                                <span style={{ color: '#242424' }}>
                                                                    {log.createdAt ? new Date(log.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                                                                </span>
                                                                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                                                    {log.createdAt ? new Date(log.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}
                                                                </span>
                                                            </div>
                                                        </td>

                                                        {/* Comments */}
                                                        <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'hidden' }}>
                                                            {comments ? (
                                                                <span style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{comments}</span>
                                                            ) : (
                                                                <span style={{ color: '#d1d5db' }}>—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                ) : null}

                    </div>{/* end task-wizard-content */}

                    {/* ── RIGHT: Help Guide Panel ── */}
                    <div className={`task-wizard-help ${helpGuideVisible ? '' : 'collapsed'}`}>
                        <button
                            type="button"
                            className="task-help-toggle"
                            onClick={() => setHelpGuideVisible(!helpGuideVisible)}
                            aria-label={helpGuideVisible ? 'Hide Guide' : 'Show Guide'}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                <circle cx="12" cy="12" r="10" />
                                {helpGuideVisible ? (
                                    <path d="M15 9l-6 3 6 3" />
                                ) : (
                                    <path d="M9 9l6 3-6 3" />
                                )}
                            </svg>
                            <span>{helpGuideVisible ? 'Hide Guide' : 'Show Guide'}</span>
                        </button>

                        {helpGuideVisible && (
                            <div className="task-help-content">
                                <div className="task-help-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                        <line x1="12" y1="17" x2="12.01" y2="17" />
                                    </svg>
                                </div>
                                <p className="task-help-title">{helpInfo.title}</p>
                                <p className="task-help-text">{helpInfo.description}</p>
                                <p className="task-help-subtitle">Tips</p>
                                <ul className="task-help-list">
                                    {helpInfo.tips.map((tip, i) => (
                                        <li key={i}>{tip}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                </div>{/* end task-wizard-layout */}

                {/* Selection Popups */}

                <WorkflowGraphPopup
    isOpen={showWorkflowGraph}
    onClose={() => setShowWorkflowGraph(false)}
    gatePassId={task.gatePassId}
    getAccessToken={getAccessToken}
    requesterName={task.requesterName}
    companyName={task.companyName}
/>
                <ApproverSelectionPopup
                    isOpen={showAdditionalApproverSelection !== null}
                    currentSelection={showAdditionalApproverSelection ? additionalApproverSelection[showAdditionalApproverSelection] : null}
                    userCompanyId={getUserInfo().companyId}
                    getAccessToken={getAccessToken}
                    onClose={() => setShowAdditionalApproverSelection(null)}
                    onConfirm={(selection) => {
                        if (showAdditionalApproverSelection) {
                            handleAdditionalApproverSelection(showAdditionalApproverSelection, selection);
                        }
                    }}
                    onStatusUpdate={handleInternalStatusUpdate}
                />

                <ReviewerSelectionPopup
                    isOpen={showAdditionalReviewerSelection !== null}
                    currentSelection={showAdditionalReviewerSelection ? additionalReviewerSelection[showAdditionalReviewerSelection] : null}
                    selectedRegionId={getCurrentTaskRegionId()}
                    getAccessToken={getAccessToken}
                    onClose={() => setShowAdditionalReviewerSelection(null)}
                    onConfirm={(selection) => {
                        if (showAdditionalReviewerSelection) {
                            handleAdditionalReviewerSelection(showAdditionalReviewerSelection, selection);
                        }
                    }}
                    onStatusUpdate={handleInternalStatusUpdate}
                />

                {/* Personnel Detail View Modal (Detail View) */}
                {(viewingPersonnel || isLoadingPersonnelDetail) && (
                    <div 
                        onClick={(e) => {
                            if (e.target === e.currentTarget && !isLoadingPersonnelDetail) {
                                setViewingPersonnel(null);
                            }
                        }}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 1050,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '24px'
                        }}
                    >
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '12px',
                            width: '100%',
                            maxWidth: '720px',
                            maxHeight: '85vh',
                            overflowY: 'auto',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {isLoadingPersonnelDetail ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '60px 24px',
                                    gap: '16px',
                                    color: '#6b7280',
                                    fontSize: '14px'
                                }}>
                                    <div style={{
                                        width: '36px',
                                        height: '36px',
                                        border: '3px solid #e5e7eb',
                                        borderTop: '3px solid #F3694A',
                                        borderRadius: '50%',
                                        animation: 'spin 0.8s linear infinite'
                                    }} />
                                    <p>Loading personnel details...</p>
                                </div>
                            ) : (
                                <PersonnelViewWrapper
                                    personnelData={viewingPersonnel}
                                    onClose={() => setViewingPersonnel(null)}
                                />
                            )}
                        </div>
                    </div>
                )}
                {/* Parent Request Detail Popup (inside detail view) */}
                {showParentPopup && parentPopupRequestId && (
                    <ParentRequestDetailPopup
                        isOpen={showParentPopup}
                        parentRequestId={parentPopupRequestId}
                        getAccessToken={getAccessToken}
                        onClose={() => { setShowParentPopup(false); setParentPopupRequestId(null); }}
                        regions={regions}
                    />
                )}

                {/* Badge View Modal — printable ID cards (inside detail view) */}
                {showBadgeView && selectedTaskForDetail && (() => {
                    const badgeTask = selectedTaskForDetail.primaryTask;
                    const badgeDetails = detailedData[badgeTask.gatePassId];
                    if (!badgeDetails || !badgeDetails.personnel) return null;

                    const allAccessCodes = new Set<string>();
                    if (badgeDetails.gates) {
                        badgeDetails.gates.forEach((gate: any) => {
                            if (gate.accessCodes && !gate.isExcluded) {
                                gate.accessCodes.split(',').map((c: string) => c.trim()).filter((c: string) => c.length > 0).forEach((code: string) => allAccessCodes.add(code));
                            }
                        });
                    }
                    let consolidatedCode = (badgeDetails as any).consolidatedAccessCode || '';
                    if (!consolidatedCode && allAccessCodes.size > 0) {
                        const sorted = [...allAccessCodes].sort((a, b) => {
                            const numA = Number(a), numB = Number(b);
                            if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                            return a.localeCompare(b);
                        });
                        consolidatedCode = sorted.join(', ');
                    }

                    const activePeople = badgeDetails.personnel.filter((p: any) => !p.isExcluded);

                    return (
                        <div
                            className="badge-overlay-backdrop"
                            onClick={(e) => { if (e.target === e.currentTarget) { setShowBadgeView(false); setBadgePhotoUrls({}); } }}
                            style={{
                                position: 'fixed',
                                top: 0, left: 0, right: 0, bottom: 0,
                                background: 'rgba(0,0,0,0.5)',
                                zIndex: 1100,
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                overflowY: 'auto',
                                padding: '40px 24px'
                            }}
                        >
                            <div className="badge-modal-container" style={{
                                background: '#ffffff',
                                borderRadius: '12px',
                                width: '100%',
                                maxWidth: '900px',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
                            }}>
                                {/* Modal Header — hidden during print */}
                                <div className="badge-modal-header" style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '20px 28px',
                                    borderBottom: '1px solid #e2e8f0'
                                }}>
                                    <div>
                                        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#242424' }}>
                                            Personnel Badges
                                        </h2>
                                        <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
                                            Request #{badgeTask.gatePassId} · {badgeDetails.companyName} · {activePeople.length} badge{activePeople.length !== 1 ? 's' : ''}
                                        </p>
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button
                                            onClick={() => window.print()}
                                            style={{
                                                padding: '8px 16px',
                                                backgroundColor: '#242424',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="6 9 6 2 18 2 18 9" />
                                                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                                                <rect x="6" y="14" width="12" height="8" />
                                            </svg>
                                            Print Badges
                                        </button>
                                        <button
                                            onClick={() => { setShowBadgeView(false); setBadgePhotoUrls({}); }}
                                            style={{
                                                padding: '8px 12px',
                                                backgroundColor: '#f3f4f6',
                                                color: '#374151',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                fontWeight: 500
                                            }}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>

                                {/* Badge Cards */}
                                <div className="badge-cards-grid" style={{
                                    padding: '28px',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(370px, 1fr))',
                                    gap: '24px'
                                }}>
                                    {loadingBadges ? (
                                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '48px', color: '#6b7280' }}>
                                            <div className="spinner" style={{ margin: '0 auto 16px' }} />
                                            <p>Loading badge data...</p>
                                        </div>
                                    ) : activePeople.map((person: any) => {
                                        const personName = person.fullName || `${person.firstName || ''} ${person.lastName || ''}`.trim() || 'Unknown';
                                        const photoUrl = badgePhotoUrls[person.personnelId];
                                        const dob = person.dateOfBirth;

                                        return (
                                            <div key={person.gatePassPersonnelId} className="badge-card" style={{
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '12px',
                                                overflow: 'hidden',
                                                backgroundColor: '#fff',
                                                pageBreakInside: 'avoid',
                                                breakInside: 'avoid' as any
                                            }}>
                                                {/* Card Top Band */}
                                                <div style={{
                                                    background: 'linear-gradient(135deg, #242424 0%, #4a4a4a 100%)',
                                                    padding: '14px 20px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between'
                                                }}>
                                                    <div style={{
                                                        fontWeight: 700,
                                                        fontSize: '13px',
                                                        color: '#ffffff',
                                                        letterSpacing: '0.5px',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        Gate Pass
                                                    </div>
                                                    <div style={{
                                                        fontSize: '11px',
                                                        color: 'rgba(255,255,255,0.75)',
                                                        fontWeight: 500
                                                    }}>
                                                        #{badgeTask.gatePassId}
                                                    </div>
                                                </div>

                                                {/* Card Body */}
                                                <div style={{ padding: '20px', display: 'flex', gap: '18px' }}>
                                                    {/* Photo */}
                                                    <div style={{
                                                        width: '90px',
                                                        height: '110px',
                                                        borderRadius: '8px',
                                                        border: '2px solid #e2e8f0',
                                                        backgroundColor: '#f8fafc',
                                                        overflow: 'hidden',
                                                        flexShrink: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        {photoUrl ? (
                                                            <img
                                                                src={photoUrl}
                                                                alt={personName}
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).parentElement!.innerHTML = '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="1.5"><circle cx="12" cy="8" r="4"/><path d="M5.5 21a6.5 6.5 0 0 1 13 0"/></svg>'; }}
                                                            />
                                                        ) : (
                                                            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                                                                <circle cx="12" cy="8" r="4" />
                                                                <path d="M5.5 21a6.5 6.5 0 0 1 13 0" />
                                                            </svg>
                                                        )}
                                                    </div>

                                                    {/* Details */}
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{
                                                            fontSize: '17px',
                                                            fontWeight: 700,
                                                            color: '#1e293b',
                                                            marginBottom: '10px',
                                                            lineHeight: 1.25
                                                        }}>
                                                            {personName}
                                                        </div>

                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            {dob && (
                                                                <div style={{ display: 'flex', gap: '8px', fontSize: '12.5px' }}>
                                                                    <span style={{ color: '#6b7280', fontWeight: 500, minWidth: '72px' }}>DOB</span>
                                                                    <span style={{ color: '#1e293b' }}>{new Date(dob).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                                                                </div>
                                                            )}
                                                            {person.nationality && (
                                                                <div style={{ display: 'flex', gap: '8px', fontSize: '12.5px' }}>
                                                                    <span style={{ color: '#6b7280', fontWeight: 500, minWidth: '72px' }}>Nationality</span>
                                                                    <span style={{ color: '#1e293b' }}>{person.nationality}</span>
                                                                </div>
                                                            )}
                                                            {badgeDetails.passIdentificationName && (
                                                                <div style={{ display: 'flex', gap: '8px', fontSize: '12.5px' }}>
                                                                    <span style={{ color: '#6b7280', fontWeight: 500, minWidth: '72px' }}>Pass Name</span>
                                                                    <span style={{ color: '#1e293b', fontWeight: 600 }}>{badgeDetails.passIdentificationName}</span>
                                                                </div>
                                                            )}
                                                            {badgeDetails.passType && (
                                                                <div style={{ display: 'flex', gap: '8px', fontSize: '12.5px' }}>
                                                                    <span style={{ color: '#6b7280', fontWeight: 500, minWidth: '72px' }}>Pass Type</span>
                                                                    <span style={{
                                                                        backgroundColor: getPassTypeColor(badgeDetails.passType) + '18',
                                                                        color: getPassTypeColor(badgeDetails.passType),
                                                                        padding: '1px 7px',
                                                                        borderRadius: '4px',
                                                                        fontSize: '11px',
                                                                        fontWeight: 600
                                                                    }}>
                                                                        {getPassTypeLabel(badgeDetails.passType)}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {consolidatedCode && (
                                                                <div style={{ display: 'flex', gap: '8px', fontSize: '12.5px', marginTop: '2px' }}>
                                                                    <span style={{ color: '#6b7280', fontWeight: 500, minWidth: '72px' }}>Access Code</span>
                                                                    <span style={{
                                                                        fontWeight: 700,
                                                                        color: '#242424',
                                                                        fontFamily: 'monospace',
                                                                        fontSize: '13px',
                                                                        letterSpacing: '0.5px'
                                                                    }}>
                                                                        {consolidatedCode}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Validity Footer */}
                                                {(badgeDetails.startDate || badgeDetails.endDate) && (
                                                    <div style={{
                                                        borderTop: '1px solid #e2e8f0',
                                                        padding: '8px 20px',
                                                        backgroundColor: '#fafbfc',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        fontSize: '11px',
                                                        color: '#6b7280'
                                                    }}>
                                                        {badgeDetails.startDate && (
                                                            <span>Valid from: <strong style={{ color: '#374151' }}>{new Date(badgeDetails.startDate).toLocaleDateString()}</strong></span>
                                                        )}
                                                        {badgeDetails.endDate && (
                                                            <span>Expires: <strong style={{ color: '#374151' }}>{new Date(badgeDetails.endDate).toLocaleDateString()}</strong></span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>
        );
    }


    // LIST VIEW — Enterprise table design
    return (
        <div className="company-management" style={{ background: '#ffffff' }}>
            {/* Success/Error Banners */}
            <SuccessMessageBanner
                message={successBanner.message}
                isVisible={successBanner.isVisible}
                onClose={closeSuccessBanner}
                autoCloseDelay={5000}
            />
            <ErrorMessageBanner
                message={errorBanner.message}
                isVisible={errorBanner.isVisible}
                onClose={closeErrorBanner}
                autoCloseDelay={7000}
            />
           

            <div className="companies-container">
                {/* Toolbar */}
                <div className="table-toolbar">
                    <div className="toolbar-left">
                        <span className="record-count">
                            {groupedSortedTasks.length} task{groupedSortedTasks.length !== 1 ? 's' : ''} pending
                        </span>
                    </div>
                    <div className="toolbar-right" style={{ gap: '10px' }}>
                        {/* Filter */}
                        <select
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                            style={{
                                height: '34px',
                                padding: '0 28px 0 10px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                backgroundColor: '#ffffff',
                                fontSize: '13px',
                                color: '#242424',
                                cursor: 'pointer',
                                outline: 'none',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                appearance: 'none',
                                backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%236b7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 10px center'
                            }}
                        >
                            <option value="all">All ({groupTasks(tasks).length})</option>
                            <option value="COMPANY_APPROVER">Company Approval ({tasks.filter(t => t.taskType === 'COMPANY_APPROVER').length})</option>
                            <option value="COMPANY_REVIEWER">Company Review ({tasks.filter(t => t.taskType === 'COMPANY_REVIEWER').length})</option>
                            <option value="GATE_APPROVER">Gate Approval ({groupTasks(tasks.filter(t => t.taskType === 'GATE_APPROVER')).length})</option>
                            <option value="GATE_REVIEWER">Gate Review ({groupTasks(tasks.filter(t => t.taskType === 'GATE_REVIEWER')).length})</option>
                            <option value="SECURITY_APPROVER">Security Approval ({tasks.filter(t => t.taskType === 'SECURITY_APPROVER').length})</option>
                        </select>

                        {/* Sort */}
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as any)}
                            style={{
                                height: '34px',
                                padding: '0 28px 0 10px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                backgroundColor: '#ffffff',
                                fontSize: '13px',
                                color: '#242424',
                                cursor: 'pointer',
                                outline: 'none',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                appearance: 'none',
                                backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%236b7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 10px center'
                            }}
                        >
                            <option value="startDate">Start Date ↓</option>
                            <option value="dueDate">Due Date</option>
                            <option value="priority">Priority</option>
                            <option value="company">Company</option>
                            <option value="taskType">Task Type</option>
                        </select>

                        {/* Refresh */}
                        <button
                            onClick={loadTasks}
                            disabled={loading}
                            className="create-company-btn"
                            style={{ opacity: loading ? 0.6 : 1 }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="23 4 23 10 17 10" />
                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                            </svg>
                            <span>{loading ? 'Loading...' : 'Refresh'}</span>
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="companies-table-container">
                    <table className="minimalist-table">
                        <thead>
                            <tr>
                                <th style={{ width: '70px' }}>REQ #</th>
                                <th>COMPANY</th>
                                <th style={{ width: '110px' }}>START DATE</th>
                                <th style={{ width: '150px' }}>PASS ID</th>
                                <th style={{ width: '150px' }}>TASK TYPE</th>
                                <th style={{ width: '140px' }}>DETAILS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupedSortedTasks.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="no-results">
                                        <div className="no-results-content">
                                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="11" cy="11" r="8" />
                                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                            </svg>
                                            <p>No tasks match the selected filter</p>
                                            <button onClick={() => setFilterBy('all')} className="clear-search-link">Show all tasks</button>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                groupedSortedTasks.map((grouped) => {
                                    const task = grouped.primaryTask;
                                    return (
                                    <tr
                                        key={grouped.taskIds.join('-')}
                                        onClick={() => handleShowGroupedTaskDetail(grouped)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td>
                                            <span style={{ color: '#242424', fontWeight: 600, fontSize: '13px' }}>
                                                {task.gatePassId}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                                <span style={{ fontWeight: 600, color: '#242424' }}>{task.companyName}</span>
                                                <span style={{ fontSize: '12px', color: '#9ca3af' }}>{task.requesterName}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{ color: '#242424' }}>
                                                {task.startDate ? new Date(task.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ color: '#242424', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                                {task.passIdentificationName || <span style={{ color: '#9ca3af' }}>—</span>}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ color: '#6b7280', fontSize: '13px' }}>
                                                {getTaskTypeLabel(task.taskType)}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ color: '#6b7280', fontSize: '12px' }}>
                                                {grouped.assignedGateNumbers.length > 1
                                                    ? `Gates ${grouped.assignedGateNumbers.join(', ')} · `
                                                    : grouped.assignedGateNumbers.length === 1
                                                        ? `Gate ${grouped.assignedGateNumbers[0]} · `
                                                        : ''
                                                }{formatRelativeTime(task.assignedAt)}
                                            </span>
                                        </td>
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="table-footer">
                    <div className="table-stats">
                        <span className="stat-item">
                            Showing <strong>{groupedSortedTasks.length}</strong> {groupedSortedTasks.length === 1 ? 'task' : 'tasks'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Personnel Detail View Modal */}
            {(viewingPersonnel || isLoadingPersonnelDetail) && (
                <div 
                    onClick={(e) => {
                        if (e.target === e.currentTarget && !isLoadingPersonnelDetail) {
                            setViewingPersonnel(null);
                        }
                    }}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.4)',
                        zIndex: 1050,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '24px'
                    }}
                >
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '8px',
                        width: '100%',
                        maxWidth: '720px',
                        maxHeight: '85vh',
                        overflowY: 'auto',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {isLoadingPersonnelDetail ? (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '60px 24px',
                                gap: '16px',
                                color: '#6b7280',
                                fontSize: '14px'
                            }}>
                                <div className="spinner" />
                                <p>Loading personnel details...</p>
                            </div>
                        ) : (
                            <PersonnelViewWrapper
                                personnelData={viewingPersonnel}
                                onClose={() => setViewingPersonnel(null)}
                            />
                        )}
                    </div>
                </div>
            )}

        </div>
    );
    };

    return (
        <>
            <h1 style={{
                fontSize: '25px',
                fontWeight: 600,
                color: '#1a1a1a',
                margin: '0 0 12px 0',
                padding: '5px 0 0 20px',
                lineHeight: 1.3,
                fontFamily: "'Segoe UI', sans-serif",
                letterSpacing: '-0.01em',
            }}>
                My Tasks
            </h1>

            {renderContent()}
        </>
    );
};